// WP-0.5 (AR-3, OP-4): API contracts — the single source of truth for server handlers
// and the web client. zod-typed routes per DEV_PLAN §2 locked toolchain.
import {
  ACCOUNT_TYPES,
  CATEGORY_KINDS,
  CONFIDENCE_LEVELS,
  EVENT_SOURCES,
  FEE_TYPES,
  GOAL_BEHAVIORS,
  GOAL_STATUSES,
  MI_EXPENSE_DIMENSIONS,
  MI_INCOME_DIMENSIONS,
  PARTY_ROLES,
  PROJECT_ADJUSTMENT_TYPES,
  PROJECT_EARNING_MODELS,
  PROJECT_STATUSES,
  QUOTATION_CONVERT_TARGETS,
  QUOTATION_STATES,
  RECUR_BEHAVIORS,
  RECUR_FREQUENCIES,
  RECUR_KINDS,
  REPORT_FAMILIES,
  RESERVE_STATUSES,
  REVENUE_BASES,
  RP_KINDS,
  SCENARIO_OPERATION_KINDS,
  SCHEDULE_ITEM_STATUSES,
  SEARCH_RESULT_KINDS,
  SUGGESTION_STATUSES,
  TIMELINE_ENTITY_TYPES,
  TPF_FEE_BEARERS,
  WATERFALL_TARGET_KINDS,
  WATERFALL_TIERS,
  YEAR_BASES,
  isCurrencyCode,
  type CurrencyCode,
} from '@pfos/engine';
import { z } from 'zod';

/** Every non-2xx response is this envelope — no bare strings, no leaked stacks (SE). */
export const errorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string().min(1),
    message: z.string().min(1),
    requestId: z.string().optional(),
    details: z.unknown().optional(),
  }),
});
export type ErrorEnvelope = z.infer<typeof errorEnvelopeSchema>;

/** OP-4: /healthz — uptime-kuma monitors this. */
export const healthzResponseSchema = z.object({
  status: z.enum(['ok', 'degraded']),
  version: z.string(),
  db: z.enum(['up', 'down']),
  uptimeSec: z.number().nonnegative(),
});
export type HealthzResponse = z.infer<typeof healthzResponseSchema>;

/** Deployment/debug identity; also proves the DB -> engine replay path is wired. */
export const metaResponseSchema = z.object({
  app: z.literal('PF-OS'),
  engineApiVersion: z.number().int(),
  eventCount: z.number().int().nonnegative(),
});
export type MetaResponse = z.infer<typeof metaResponseSchema>;

/** WP-0.5 contract round-trip fixture route; retired once real routes exist (WP-1.1+). */
export const echoRequestSchema = z.object({ text: z.string().min(1).max(10_000) });
export const echoResponseSchema = z.object({ text: z.string() });
export type EchoRequest = z.infer<typeof echoRequestSchema>;
export type EchoResponse = z.infer<typeof echoResponseSchema>;

// --- WP-0.6 auth contracts (§16.4 AU-A/B/C, S-01/02/03) --------------------------------

export const authStatusResponseSchema = z.object({
  ownerExists: z.boolean(),
  authenticated: z.boolean(),
  onboarded: z.boolean(), // WP-1.19 (OB-1): S-04 finished — gates the onboarding redirect
});
export type AuthStatusResponse = z.infer<typeof authStatusResponseSchema>;

export const authSetupStartRequestSchema = z.object({
  setupToken: z.string().min(1),
  name: z.string().min(1).max(100),
  password: z.string().min(12).max(200), // AU-B "strong password" — 12+ chars
});
export const authSetupStartResponseSchema = z.object({
  totpSecret: z.string().min(1),
  otpauthUrl: z.string().min(1),
  webauthnOptions: z.unknown(),
});
export const authSetupFinishRequestSchema = z.object({
  setupToken: z.string().min(1),
  totpCode: z.string().regex(/^\d{6}$/),
  attestation: z.unknown().optional(), // passkey optional at setup; fallback-only allowed
});

export const authLoginPasswordRequestSchema = z.object({
  password: z.string().min(1),
  totpCode: z.string().regex(/^\d{6}$/),
});
export const authPasskeyFinishRequestSchema = z.object({
  assertion: z.unknown(),
});

export const sessionListResponseSchema = z.object({
  sessions: z.array(
    z.object({
      id: z.string(),
      createdAt: z.string(),
      lastSeenAt: z.string(),
      userAgent: z.string().nullable(),
      current: z.boolean(), // S-03 "this-device" tag
    }),
  ),
});
export type SessionListResponse = z.infer<typeof sessionListResponseSchema>;

// --- WP-C1.7 (ST-4, SE-1): app-lock PIN — a gate on an ALREADY-authenticated session, not a
// second login. 4-6 digits only (a quick re-entry PIN, not password-strength). -----------------
const pinSchema = z.string().regex(/^\d{4,6}$/, 'PIN must be 4-6 digits');
export const appLockSetPinRequestSchema = z.object({ pin: pinSchema });
export const appLockVerifyPinRequestSchema = z.object({ pin: pinSchema });
export const appLockStatusResponseSchema = z.object({ enabled: z.boolean() });
export type AppLockStatusResponse = z.infer<typeof appLockStatusResponseSchema>;

// --- WP-1.1 account contracts (§18.5 Account form, AC-1/AC-2, S-11/S-12) ---------------

/** AC-1 account types — the same enum the engine enforces. */
export const accountTypeSchema = z.enum(ACCOUNT_TYPES);

/** Validates against the engine's own currency registry (never drifts from what it accepts). */
const currencyCodeSchema = z.custom<CurrencyCode>(
  (v) => typeof v === 'string' && isCurrencyCode(v),
  { message: 'Unknown currency code' },
);

export const accountResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: accountTypeSchema,
  currency: currencyCodeSchema,
  openingMinor: z.number().int(),
  openingAsOf: z.string(),
  spendable: z.boolean(), // AC-4
  archived: z.boolean(),
  displayOrder: z.number().int(), // AC-2
  platformGroup: z.string().optional(),
  icon: z.string().optional(),
  color: z.string().optional(),
  balanceMinor: z.number().int(),
});
export type AccountResponse = z.infer<typeof accountResponseSchema>;

export const accountListResponseSchema = z.object({ accounts: z.array(accountResponseSchema) });
export type AccountListResponse = z.infer<typeof accountListResponseSchema>;

export const accountCreateRequestSchema = z.object({
  name: z.string().min(1).max(100),
  type: accountTypeSchema,
  currency: currencyCodeSchema,
  openingMinor: z.number().int(),
  openingAsOf: z.string().min(1),
  platformGroup: z.string().min(1).max(60).optional(),
  spendable: z.boolean().optional(),
  icon: z.string().min(1).max(40).optional(),
  color: z.string().min(1).max(20).optional(),
});
export type AccountCreateRequest = z.infer<typeof accountCreateRequestSchema>;

/** §18.5: "currency (locked after first transaction)" — the engine enforces the lock;
 * the shape here just allows the field so an unlocked edit (no transactions yet) can pass it. */
export const accountEditRequestSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  type: accountTypeSchema.optional(),
  currency: currencyCodeSchema.optional(),
  platformGroup: z.string().min(1).max(60).optional(),
  spendable: z.boolean().optional(),
  icon: z.string().min(1).max(40).optional(),
  color: z.string().min(1).max(20).optional(),
});
export type AccountEditRequest = z.infer<typeof accountEditRequestSchema>;

// --- WP-1.2 transaction contracts (M6 TX-1..3/5, EX-1, S-09/S-10) ----------------------

export const transactionTypeSchema = z.enum(['expense', 'income']);
const eventSourceSchema = z.enum(EVENT_SOURCES);

// TX-4/PJ-6: one line per split; linkType/linkId are generic plumbing until each linkable
// entity (project/schedule/R&P/TPF) is built in a later WP.
export const transactionLineSchema = z.object({
  amountMinor: z.number().int(),
  note: z.string().min(1).max(500).optional(),
  category: z.string().min(1).max(80).optional(),
  linkType: z.string().min(1).max(40).optional(),
  linkId: z.string().min(1).max(100).optional(),
});
export type TransactionLine = z.infer<typeof transactionLineSchema>;

// FE-1: typed fee legs (withholding/levy is a first-class type — Pakistani bank reality).
export const feeTypeSchema = z.enum(FEE_TYPES);
export const feeLegSchema = z.object({
  type: feeTypeSchema,
  amountMinor: z.number().int().positive(),
});
export type FeeLeg = z.infer<typeof feeLegSchema>;

export const transactionResponseSchema = z.object({
  id: z.string(),
  type: transactionTypeSchema,
  accountId: z.string(),
  currency: currencyCodeSchema,
  amountMinor: z.number().int(),
  at: z.string(), // TX-3 datetime
  category: z.string().optional(), // EX-1; a real CategoryPicker/tree lands in WP-1.7
  party: z.string().optional(), // TX-3 "optional counterparty" — the name as typed
  partyId: z.string().optional(), // WP-3.1: the live link
  projectId: z.string().optional(), // WP-3.3 (PJ-6/PJ-7): the simple direct project link
  scheduleItemId: z.string().optional(), // WP-3.4 (SC-1/SC-5): the simple direct schedule item link
  note: z.string().optional(),
  lines: z.array(transactionLineSchema).optional(), // TX-4 splits
  feeLegs: z.array(feeLegSchema).optional(), // FE-1/FE-2
  tagIds: z.array(z.string()).optional(), // ST-2 TagInput (WP-1.7)
  reversed: z.boolean(),
  source: eventSourceSchema,
  backfill: z.boolean().optional(), // OB-3: entered as historical data via CSV import (WP-1.13)
  adjustment: z.boolean().optional(), // TX-1: reconciliation-only Adjustment (WP-1.14)
  duplicateOf: z.string().optional(), // TX-6: possible-duplicate suspect id (WP-1.16)
});
export type TransactionResponse = z.infer<typeof transactionResponseSchema>;

export const transactionListResponseSchema = z.object({
  transactions: z.array(transactionResponseSchema),
});
export type TransactionListResponse = z.infer<typeof transactionListResponseSchema>;

/** LG-3 AuditTrail: one entry per event in the transaction's history, oldest first. */
export const transactionHistoryEntrySchema = z.object({
  kind: z.string(),
  at: z.string(),
  source: eventSourceSchema,
  reason: z.string().optional(),
});
export const transactionDetailResponseSchema = transactionResponseSchema.extend({
  history: z.array(transactionHistoryEntrySchema),
});
export type TransactionDetailResponse = z.infer<typeof transactionDetailResponseSchema>;

export const transactionCreateRequestSchema = z.object({
  type: transactionTypeSchema,
  accountId: z.string().min(1),
  amountMinor: z.number().int().positive(),
  /** Optional client-supplied idempotency key (§14-13: a retry with the same id is safe). */
  id: z.string().min(1).optional(),
  /** Optional backdate; the API defaults to "now" when omitted (§18.5 Quick Expense). */
  at: z.string().min(1).optional(),
  category: z.string().min(1).max(80).optional(),
  party: z.string().min(1).max(120).optional(),
  partyId: z.string().min(1).optional(), // WP-3.1
  projectId: z.string().min(1).optional(), // WP-3.3
  scheduleItemId: z.string().min(1).optional(), // WP-3.4
  note: z.string().min(1).max(500).optional(),
  /** TX-4: Σ lines must equal amountMinor — the engine is the source of truth on this. */
  lines: z.array(transactionLineSchema).min(2).optional(),
  feeLegs: z.array(feeLegSchema).optional(), // FE-1/FE-2
  tagIds: z.array(z.string().min(1)).optional(), // ST-2 TagInput (WP-1.7)
});
export type TransactionCreateRequest = z.infer<typeof transactionCreateRequestSchema>;

export const transactionReverseRequestSchema = z.object({
  reason: z.string().min(10, 'Give a reason (10+ characters) so the history stays useful'),
});
export type TransactionReverseRequest = z.infer<typeof transactionReverseRequestSchema>;

// --- WP-1.4 fee query (FE-3: total fees; fees by type; fees by platform/channel) -------

export const feeSummaryResponseSchema = z.object({
  totalMinor: z.number().int(),
  // Not every FeeType necessarily appears — a plain string-keyed record avoids zod v4's
  // exhaustive-record requirement for enum-keyed z.record() (every key would be mandatory).
  byType: z.record(z.string(), z.number().int()),
});
export type FeeSummaryResponse = z.infer<typeof feeSummaryResponseSchema>;

// --- WP-1.5 transfer contracts (M9 TR-1..4) ---------------------------------------------

export const transferStatusSchema = z.enum(['in_transit', 'settled']);

export const transferResponseSchema = z.object({
  id: z.string(),
  fromAccountId: z.string(),
  toAccountId: z.string(),
  sentMinor: z.number().int(),
  sentCurrency: currencyCodeSchema,
  feeLegs: z.array(feeLegSchema).optional(),
  status: transferStatusSchema,
  initiatedAt: z.string(),
  note: z.string().optional(),
  receivedMinor: z.number().int().optional(),
  settledAt: z.string().optional(),
  estimatedReceivedMinor: z.number().int().optional(), // FX-1
  conversionLossMinor: z.number().int().optional(), // FX-4
});
export type TransferResponse = z.infer<typeof transferResponseSchema>;

export const transferListResponseSchema = z.object({ transfers: z.array(transferResponseSchema) });
export type TransferListResponse = z.infer<typeof transferListResponseSchema>;

export const transferCreateRequestSchema = z.object({
  /** Optional client-supplied idempotency key (§14-13), same pattern as transactions. */
  id: z.string().min(1).optional(),
  fromAccountId: z.string().min(1),
  toAccountId: z.string().min(1),
  sentMinor: z.number().int().positive(),
  feeLegs: z.array(feeLegSchema).optional(),
  note: z.string().min(1).max(500).optional(),
  /** §18.5 "arrived?" toggle ON at creation — settle immediately with this amount. */
  receivedMinor: z.number().int().positive().optional(),
});
export type TransferCreateRequest = z.infer<typeof transferCreateRequestSchema>;

export const transferSettleRequestSchema = z.object({
  receivedMinor: z.number().int().positive(),
});
export type TransferSettleRequest = z.infer<typeof transferSettleRequestSchema>;

export const inTransitTotalResponseSchema = z.object({ totalMinor: z.number().int() });
export type InTransitTotalResponse = z.infer<typeof inTransitTotalResponseSchema>;

// --- WP-1.6 FX contracts (M7 FX-1..4, CFG-1) ---------------------------------------------

export const fxRateSourceSchema = z.enum(['manual', 'fetched']);

export const fxRateResponseSchema = z.object({
  from: currencyCodeSchema,
  to: currencyCodeSchema,
  rate: z.number().positive(),
  source: fxRateSourceSchema,
  asOf: z.string(),
});
export type FxRateResponse = z.infer<typeof fxRateResponseSchema>;

export const fxRateSetRequestSchema = z.object({
  from: currencyCodeSchema,
  to: currencyCodeSchema,
  rate: z.number().positive(),
});
export type FxRateSetRequest = z.infer<typeof fxRateSetRequestSchema>;

export const conversionLossResponseSchema = z.object({ totalMinor: z.number().int() });
export type ConversionLossResponse = z.infer<typeof conversionLossResponseSchema>;

// --- WP-1.7 category & tag contracts (EX-2, ST-2) ---------------------------------------

export const categoryKindSchema = z.enum(CATEGORY_KINDS);

export const categoryResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: categoryKindSchema,
  parentId: z.string().optional(),
  archived: z.boolean(),
  displayOrder: z.number().int(),
  essential: z.boolean().optional(), // WP-6.1 (GL-7/GL-8)
});
export type CategoryResponse = z.infer<typeof categoryResponseSchema>;

export const categoryListResponseSchema = z.object({ categories: z.array(categoryResponseSchema) });
export type CategoryListResponse = z.infer<typeof categoryListResponseSchema>;

export const categoryCreateRequestSchema = z.object({
  name: z.string().min(1).max(80),
  kind: categoryKindSchema,
  parentId: z.string().min(1).optional(),
});
export type CategoryCreateRequest = z.infer<typeof categoryCreateRequestSchema>;

/** §18.5 tree editor: rename/move/archive/essential, one partial patch (parentId: null moves
 * to root). `essential` is WP-6.1's own addition (GL-7/GL-8). */
export const categoryEditRequestSchema = z.object({
  name: z.string().min(1).max(80).optional(),
  parentId: z.string().min(1).nullable().optional(),
  archived: z.boolean().optional(),
  essential: z.boolean().optional(),
});
export type CategoryEditRequest = z.infer<typeof categoryEditRequestSchema>;

/** The full new order of one sibling group (EX-2 "re-orderable"). */
export const categoryReorderRequestSchema = z.object({
  ids: z.array(z.string().min(1)).min(1),
});
export type CategoryReorderRequest = z.infer<typeof categoryReorderRequestSchema>;

/** EX-2 "bulk re-categorization" — every non-reversed transaction tagged `from` moves to `to`. */
export const categoryReassignRequestSchema = z.object({
  from: z.string().min(1),
  to: z.string().min(1),
  accountId: z.string().min(1).optional(),
});
export type CategoryReassignRequest = z.infer<typeof categoryReassignRequestSchema>;

export const categoryReassignResponseSchema = z.object({ updated: z.number().int().nonnegative() });
export type CategoryReassignResponse = z.infer<typeof categoryReassignResponseSchema>;

export const tagResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  archived: z.boolean(),
});
export type TagResponse = z.infer<typeof tagResponseSchema>;

export const tagListResponseSchema = z.object({ tags: z.array(tagResponseSchema) });
export type TagListResponse = z.infer<typeof tagListResponseSchema>;

export const tagCreateRequestSchema = z.object({ name: z.string().min(1).max(40) });
export type TagCreateRequest = z.infer<typeof tagCreateRequestSchema>;

export const tagEditRequestSchema = z.object({
  name: z.string().min(1).max(40).optional(),
  archived: z.boolean().optional(),
});
export type TagEditRequest = z.infer<typeof tagEditRequestSchema>;

// --- WP-1.8 receivables & payables contracts (M11 RP-1..4) ------------------------------

export const rpKindSchema = z.enum(RP_KINDS);
export const rpStatusSchema = z.enum(['open', 'settled', 'closed']);

export const rpSettlementSchema = z.object({
  amountMinor: z.number().int(),
  transactionId: z.string(),
  at: z.string(),
});
export type RpSettlement = z.infer<typeof rpSettlementSchema>;

export const rpResponseSchema = z.object({
  id: z.string(),
  kind: rpKindSchema,
  party: z.string(),
  partyId: z.string().optional(), // WP-3.1
  amountMinor: z.number().int(),
  currency: currencyCodeSchema,
  reason: z.string(),
  createdAt: z.string(),
  dueDate: z.string().optional(),
  notes: z.string().optional(),
  settlements: z.array(rpSettlementSchema),
  settledMinor: z.number().int(),
  status: rpStatusSchema,
  closedReason: z.string().optional(),
  closedAt: z.string().optional(),
});
export type RpResponse = z.infer<typeof rpResponseSchema>;

export const rpListResponseSchema = z.object({ rps: z.array(rpResponseSchema) });
export type RpListResponse = z.infer<typeof rpListResponseSchema>;

/** RP-1 locked fields (§2.13): party, amount, currency, reason, optional due date, notes. */
export const rpCreateRequestSchema = z.object({
  kind: rpKindSchema,
  party: z.string().min(1).max(120),
  partyId: z.string().min(1).optional(), // WP-3.1
  amountMinor: z.number().int().positive(),
  currency: currencyCodeSchema,
  reason: z.string().min(1).max(500),
  dueDate: z.string().min(1).optional(),
  notes: z.string().min(1).max(1000).optional(),
});
export type RpCreateRequest = z.infer<typeof rpCreateRequestSchema>;

/** §18.5 "Settle: amount ≤ remaining · link transaction". */
export const rpSettleRequestSchema = z.object({
  amountMinor: z.number().int().positive(),
  transactionId: z.string().min(1),
});
export type RpSettleRequest = z.infer<typeof rpSettleRequestSchema>;

/** §18.5 "Write-off/Waive: reason required, ConfirmDialog types amount." */
export const rpCloseRequestSchema = z.object({
  reason: z.string().min(1).max(500),
});
export type RpCloseRequest = z.infer<typeof rpCloseRequestSchema>;

/** WP-5.5 (OB-3 Data Health guided fix): sets/changes an RP's due date. */
export const rpDueDateRequestSchema = z.object({
  dueDate: z.string().min(1),
});
export type RpDueDateRequest = z.infer<typeof rpDueDateRequestSchema>;

// --- WP-6.1 goals & reserves contracts (M28 GL-1..8, S-31 `/goals`) ----------------------

export const goalBehaviorSchema = z.enum(GOAL_BEHAVIORS);
export const goalStatusSchema = z.enum(GOAL_STATUSES);

/** GL-1's own "allocated amount, remaining" are DERIVED (see GoalView's own engine-side
 * header comment) — this response always carries them, never just the raw stored fields. */
export const goalResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  targetAmountMinor: z.number().int(),
  currency: currencyCodeSchema,
  targetDate: z.string().optional(),
  priority: z.number().int(),
  minimumContributionMinor: z.number().int().optional(),
  behavior: goalBehaviorSchema,
  status: goalStatusSchema,
  createdAt: z.string(),
  allocatedMinor: z.number().int(),
  remainingMinor: z.number().int(),
  completed: z.boolean(),
});
export type GoalResponse = z.infer<typeof goalResponseSchema>;

export const goalListResponseSchema = z.object({ goals: z.array(goalResponseSchema) });
export type GoalListResponse = z.infer<typeof goalListResponseSchema>;

/** GL-1 locked fields (§2.32). */
export const goalCreateRequestSchema = z.object({
  name: z.string().min(1).max(120),
  targetAmountMinor: z.number().int().positive(),
  currency: currencyCodeSchema,
  targetDate: z.string().min(1).optional(),
  priority: z.number().int().positive(),
  minimumContributionMinor: z.number().int().positive().optional(),
  behavior: goalBehaviorSchema,
});
export type GoalCreateRequest = z.infer<typeof goalCreateRequestSchema>;

/** GL-3: "flexible goals let the advisor reorganize plans" — every field stays editable. */
export const goalEditRequestSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  targetAmountMinor: z.number().int().positive().optional(),
  targetDate: z.string().min(1).optional(),
  priority: z.number().int().positive().optional(),
  minimumContributionMinor: z.number().int().positive().optional(),
  behavior: goalBehaviorSchema.optional(),
});
export type GoalEditRequest = z.infer<typeof goalEditRequestSchema>;

export const reserveStatusSchema = z.enum(RESERVE_STATUSES);

export const reserveResponseSchema = z.object({
  id: z.string(),
  purpose: z.string(),
  amountMinor: z.number().int(),
  currency: currencyCodeSchema,
  accountId: z.string().optional(),
  goalId: z.string().optional(),
  rpId: z.string().optional(), // WP-6.3 (AD-3): a "planned settlement" — a reserve linked to an RP.
  status: reserveStatusSchema,
  createdAt: z.string(),
  releasedAt: z.string().optional(),
  releasedReason: z.string().optional(),
});
export type ReserveResponse = z.infer<typeof reserveResponseSchema>;

export const reserveListResponseSchema = z.object({ reserves: z.array(reserveResponseSchema) });
export type ReserveListResponse = z.infer<typeof reserveListResponseSchema>;

/** GL-4 "the concrete mechanism behind 'keep this aside'". */
export const reserveCreateRequestSchema = z.object({
  purpose: z.string().min(1).max(200),
  amountMinor: z.number().int().positive(),
  currency: currencyCodeSchema,
  accountId: z.string().min(1).optional(),
  goalId: z.string().min(1).optional(),
  rpId: z.string().min(1).optional(),
});
export type ReserveCreateRequest = z.infer<typeof reserveCreateRequestSchema>;

/** GL-4: "releasable anytime ... all changes are logged" — a reason is required. */
export const reserveReleaseRequestSchema = z.object({
  reason: z.string().min(1).max(500),
});
export type ReserveReleaseRequest = z.infer<typeof reserveReleaseRequestSchema>;

/** GL-2 (LOCKED): "bind the Spendable-Now computation and force advisor warnings on
 * violation" — also embedded verbatim in `GET /api/v1/dashboard`'s own response. */
export const strictReserveViolationSchema = z.object({
  currency: currencyCodeSchema,
  requiredMinor: z.number().int(),
  availableMinor: z.number().int(),
  violated: z.boolean(),
});
export type StrictReserveViolation = z.infer<typeof strictReserveViolationSchema>;

/** §19.4 (GL-6..8, OD-4): the reserve ramp-up computation. */
export const reserveRampUpStatusSchema = z.object({
  currency: currencyCodeSchema,
  daysOfData: z.number().int().nonnegative(),
  avgMonthlyEssentialMinor: z.number().int().nonnegative(),
  targetProposalEligible: z.boolean(),
  suggestedTargetMinor: z.number().int().nonnegative(),
  lowSpendableAlertActive: z.boolean(),
  lowSpendableThresholdMinor: z.number().int().nonnegative(),
});
export type ReserveRampUpStatus = z.infer<typeof reserveRampUpStatusSchema>;

// WP-6.2 (M29 AD-2, LOCKED §2.31): the priority waterfall — a pure computation, no accept/
// reject state here (WP-6.3's own "Accept creates reserves/planned settlements only" is the
// write path this feeds).
export const waterfallTierSchema = z.enum(WATERFALL_TIERS);
export const waterfallTargetKindSchema = z.enum(WATERFALL_TARGET_KINDS);

export const waterfallLineSchema = z.object({
  tier: waterfallTierSchema,
  label: z.string(),
  allocatedMinor: z.number().int(),
  targetKind: waterfallTargetKindSchema,
  targetId: z.string().optional(),
});
export type WaterfallLine = z.infer<typeof waterfallLineSchema>;

export const waterfallAllocationResponseSchema = z.object({
  currency: currencyCodeSchema,
  incomeMinor: z.number().int(),
  lines: z.array(waterfallLineSchema),
});
export type WaterfallAllocationResponse = z.infer<typeof waterfallAllocationResponseSchema>;

// WP-6.3 (M29 AD-3/6/7, S-32): allocation suggestions — "Accept creates reserves/planned
// settlements only" (AD-7 boundary: never a transaction/transfer). See
// `packages/engine/src/domain/ledger.ts`'s own SuggestionRecord header comment for the full
// design rationale (lines are a snapshot, not re-derived at query time).
export const suggestionStatusSchema = z.enum(SUGGESTION_STATUSES);

export const suggestionResponseSchema = z.object({
  id: z.string(),
  kind: z.literal('allocation'),
  currency: currencyCodeSchema,
  incomeMinor: z.number().int(),
  asOf: z.string(),
  lines: z.array(waterfallLineSchema),
  status: suggestionStatusSchema,
  createdAt: z.string(),
  snoozedUntil: z.string().optional(),
  resolvedAt: z.string().optional(),
  createdReserveIds: z.array(z.string()),
});
export type SuggestionResponse = z.infer<typeof suggestionResponseSchema>;

export const suggestionListResponseSchema = z.object({
  suggestions: z.array(suggestionResponseSchema),
});
export type SuggestionListResponse = z.infer<typeof suggestionListResponseSchema>;

/** AD-3: the "New-income trigger" — the web/API layer calls this right after recording new
 * income; `asOf` defaults to now server-side when omitted. */
export const suggestionAllocationCreateRequestSchema = z.object({
  incomeMinor: z.number().int().positive(),
  currency: currencyCodeSchema,
  asOf: z.string().min(1).optional(),
});
export type SuggestionAllocationCreateRequest = z.infer<
  typeof suggestionAllocationCreateRequestSchema
>;

/** AD-6: snoozable — a required future resurface date. */
export const suggestionSnoozeRequestSchema = z.object({
  snoozedUntil: z.string().min(1),
});
export type SuggestionSnoozeRequest = z.infer<typeof suggestionSnoozeRequestSchema>;

/** WP-6.4 (M29 AD-4, LOCKED §2.34): "Can I afford this?" — the four-part answer (can/advisable/
 * impact/timing). A pure query, no accept/reject state (unlike Suggestion — there is nothing to
 * resolve here, it is asked fresh every time). */
export const affordabilityResponseSchema = z.object({
  currency: currencyCodeSchema,
  amountMinor: z.number().int(),
  purpose: z.string(),
  asOf: z.string(),
  spendableNowMinor: z.number().int(),
  canPayNow: z.boolean(),
  protectedFloorMinor: z.number().int(),
  remainingAfterMinor: z.number().int(),
  advisable: z.boolean(),
  futureImpactMinor: z.number().int(),
  upcomingInflowMinor: z.number().int(),
  waitingWouldHelp: z.boolean(),
});
export type AffordabilityResponse = z.infer<typeof affordabilityResponseSchema>;

export const affordabilityRequestSchema = z.object({
  amountMinor: z.number().int().positive(),
  currency: currencyCodeSchema,
  purpose: z.string().min(1).max(200),
  asOf: z.string().min(1).optional(),
});
export type AffordabilityRequest = z.infer<typeof affordabilityRequestSchema>;

// WP-7.1 (M30 SM-1..4, LOCKED §2.35, S-33 `/simulator`, §14-11): the scenario simulator.
// `runScenario` is a pure query (never a write, SM-2's own "enforced structurally") — these
// schemas mirror its exact input/output shape, no accept/reject state of their own.
export const scenarioOperationKindSchema = z.enum(SCENARIO_OPERATION_KINDS);

export const scenarioOperationSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('delay'),
    rpId: z.string().min(1),
    days: z.number().int().positive(),
  }),
  z.object({
    kind: z.literal('change'),
    rpId: z.string().min(1),
    newAmountMinor: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal('add'),
    cashflowKind: z.enum(['expense', 'income']),
    amountMinor: z.number().int().positive(),
    at: z.string().min(1),
    label: z.string().min(1).max(200),
  }),
  z.object({ kind: z.literal('payAll') }),
  z.object({ kind: z.literal('solveSpend') }),
  z.object({ kind: z.literal('solveSaving'), goalId: z.string().min(1) }),
]);
export type ScenarioOperation = z.infer<typeof scenarioOperationSchema>;

export const scenarioPointSchema = z.object({ date: z.string(), balanceMinor: z.number().int() });
export const scenarioBreachSchema = z.object({ date: z.string(), balanceMinor: z.number().int() });

export const scenarioResultResponseSchema = z.object({
  currency: currencyCodeSchema,
  asOf: z.string(),
  windowDays: z.number().int().positive(),
  points: z.array(scenarioPointSchema),
  reserveFloorMinor: z.number().int(),
  breaches: z.array(scenarioBreachSchema),
  solvedAmountMinor: z.number().int().optional(),
});
export type ScenarioResultResponse = z.infer<typeof scenarioResultResponseSchema>;

export const runScenarioRequestSchema = z.object({
  currency: currencyCodeSchema,
  asOf: z.string().min(1).optional(),
  windowDays: z.number().int().positive(),
  operations: z.array(scenarioOperationSchema),
});
export type RunScenarioRequest = z.infer<typeof runScenarioRequestSchema>;

// SM-4 "MAY be saved/reloaded" — a plain, editable/deletable named config, the SAME shape
// `savedView` (WP-5.2) already established for owner-configured, non-financial JSON blobs; the
// backend never inspects the config's own contents (the web layer alone interprets it as a
// RunScenarioRequest-shaped payload).
export const savedScenarioSchema = z.object({
  id: z.string(),
  name: z.string(),
  config: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
});
export type SavedScenario = z.infer<typeof savedScenarioSchema>;

export const savedScenarioCreateRequestSchema = z.object({
  name: z.string().min(1).max(100),
  config: z.record(z.string(), z.unknown()),
});
export type SavedScenarioCreateRequest = z.infer<typeof savedScenarioCreateRequestSchema>;

export const savedScenarioListResponseSchema = z.object({
  scenarios: z.array(savedScenarioSchema),
});
export type SavedScenarioListResponse = z.infer<typeof savedScenarioListResponseSchema>;

// --- WP-1.9 third-party funds contracts (M10 TP-1..6, FE-4) ------------------------------

export const tpfFeeBearerSchema = z.enum(TPF_FEE_BEARERS);
export const tpfFeeSchema = z.object({
  amountMinor: z.number().int().positive(),
  bearer: tpfFeeBearerSchema,
});
export type TpfFee = z.infer<typeof tpfFeeSchema>;

export const tpfMovementKindSchema = z.enum(['receive', 'return', 'convert']);

export const tpfMovementSchema = z.object({
  kind: tpfMovementKindSchema,
  caseAmountMinor: z.number().int(),
  accountId: z.string().optional(), // absent for 'convert'
  accountAmountMinor: z.number().int().optional(),
  fee: tpfFeeSchema.optional(),
  reason: z.string().optional(), // 'convert' only
  note: z.string().optional(),
  at: z.string(),
});
export type TpfMovement = z.infer<typeof tpfMovementSchema>;

export const tpfCaseResponseSchema = z.object({
  id: z.string(),
  party: z.string(),
  partyId: z.string().optional(), // WP-3.1
  currency: currencyCodeSchema,
  movements: z.array(tpfMovementSchema),
  receivedMinor: z.number().int(),
  chargedFeesMinor: z.number().int(),
  returnedMinor: z.number().int(),
  convertedMinor: z.number().int(),
  owedBackMinor: z.number().int(),
  status: z.enum(['open', 'closed']),
  closedAt: z.string().optional(),
});
export type TpfCaseResponse = z.infer<typeof tpfCaseResponseSchema>;

export const tpfCaseListResponseSchema = z.object({ cases: z.array(tpfCaseResponseSchema) });
export type TpfCaseListResponse = z.infer<typeof tpfCaseListResponseSchema>;

/** TP-1 locked fields (§2.12). */
export const tpfCaseCreateRequestSchema = z.object({
  party: z.string().min(1).max(120),
  partyId: z.string().min(1).optional(), // WP-3.1
  currency: currencyCodeSchema,
});
export type TpfCaseCreateRequest = z.infer<typeof tpfCaseCreateRequestSchema>;

/** §18.5 "TPF movement: direction · amount · account · fee {amount, bearer} · note." */
export const tpfMovementRequestSchema = z.object({
  caseAmountMinor: z.number().int().positive(),
  accountId: z.string().min(1),
  accountAmountMinor: z.number().int().positive(),
  fee: tpfFeeSchema.optional(),
  note: z.string().min(1).max(500).optional(),
});
export type TpfMovementRequest = z.infer<typeof tpfMovementRequestSchema>;

/** §18.5 "Convert-to-income: amount ≤ held, reason." */
export const tpfConvertRequestSchema = z.object({
  caseAmountMinor: z.number().int().positive(),
  reason: z.string().min(1).max(500),
});
export type TpfConvertRequest = z.infer<typeof tpfConvertRequestSchema>;

export const holdingForOthersResponseSchema = z.object({ totalMinor: z.number().int() });
export type HoldingForOthersResponse = z.infer<typeof holdingForOthersResponseSchema>;

// --- WP-1.10 recurring contracts (M13 RC-1..4, S-18) -------------------------------------

export const monthlyRuleSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('day'), day: z.number().int().min(1).max(31) }),
  z.object({ kind: z.literal('firstWeekday'), weekday: z.number().int().min(0).max(6) }),
  z.object({ kind: z.literal('lastDay') }),
]);
export type MonthlyRule = z.infer<typeof monthlyRuleSchema>;

export const recurFrequencySchema = z.enum(RECUR_FREQUENCIES);

export const recurScheduleSchema = z.object({
  frequency: recurFrequencySchema,
  everyN: z.number().int().positive(),
  monthlyRule: monthlyRuleSchema.optional(),
  weekday: z.number().int().min(0).max(6).optional(),
});
export type RecurSchedule = z.infer<typeof recurScheduleSchema>;

export const recurKindSchema = z.enum(RECUR_KINDS);
export const recurBehaviorSchema = z.enum(RECUR_BEHAVIORS);

/** §18.5 "base form embedded" — one shape per template kind. */
export const recurBaseSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('expense'),
    accountId: z.string().min(1),
    amountMinor: z.number().int().positive().optional(),
    category: z.string().min(1).max(80).optional(),
    note: z.string().min(1).max(500).optional(),
  }),
  z.object({
    kind: z.literal('income'),
    accountId: z.string().min(1),
    amountMinor: z.number().int().positive().optional(),
    category: z.string().min(1).max(80).optional(),
    note: z.string().min(1).max(500).optional(),
  }),
  z.object({
    kind: z.literal('transfer'),
    fromAccountId: z.string().min(1),
    toAccountId: z.string().min(1),
    amountMinor: z.number().int().positive().optional(),
    note: z.string().min(1).max(500).optional(),
  }),
  z.object({
    kind: z.literal('payable'),
    party: z.string().min(1).max(120),
    amountMinor: z.number().int().positive().optional(),
    currency: currencyCodeSchema,
    reason: z.string().min(1).max(500),
  }),
]);
export type RecurBase = z.infer<typeof recurBaseSchema>;

export const recurringTemplateResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  base: recurBaseSchema,
  schedule: recurScheduleSchema,
  behavior: recurBehaviorSchema,
  status: z.enum(['active', 'paused']),
  nextRunAt: z.string(),
  lastAmountMinor: z.number().int().optional(),
  createdAt: z.string(),
});
export type RecurringTemplateResponse = z.infer<typeof recurringTemplateResponseSchema>;

export const recurringTemplateListResponseSchema = z.object({
  templates: z.array(recurringTemplateResponseSchema),
});
export type RecurringTemplateListResponse = z.infer<typeof recurringTemplateListResponseSchema>;

export const recurringTemplateCreateRequestSchema = z.object({
  name: z.string().min(1).max(120),
  base: recurBaseSchema,
  schedule: recurScheduleSchema,
  behavior: recurBehaviorSchema,
  startDate: z.string().min(1),
});
export type RecurringTemplateCreateRequest = z.infer<typeof recurringTemplateCreateRequestSchema>;

/** §18.5 "edit-future-occurrences" — a partial patch; every field is optional. */
export const recurringTemplateEditRequestSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  base: recurBaseSchema.optional(),
  schedule: recurScheduleSchema.optional(),
  behavior: recurBehaviorSchema.optional(),
});
export type RecurringTemplateEditRequest = z.infer<typeof recurringTemplateEditRequestSchema>;

/** RC-2 "confirm": amountMinor is required for a variable template, optional for a fixed one. */
export const recurringPostRequestSchema = z.object({
  amountMinor: z.number().int().positive().optional(),
});
export type RecurringPostRequest = z.infer<typeof recurringPostRequestSchema>;

export const upcomingOccurrenceSchema = z.object({
  templateId: z.string(),
  name: z.string(),
  kind: recurKindSchema,
  amountMinor: z.number().int().optional(),
  at: z.string(),
});
export type UpcomingOccurrence = z.infer<typeof upcomingOccurrenceSchema>;

export const upcomingOccurrencesResponseSchema = z.object({
  occurrences: z.array(upcomingOccurrenceSchema),
});
export type UpcomingOccurrencesResponse = z.infer<typeof upcomingOccurrencesResponseSchema>;

// --- WP-1.11 dashboard contracts (M16 DB-1..5, S-05) -------------------------------------

export const dashboardResponseSchema = z.object({
  currency: currencyCodeSchema,
  availableMinor: z.number().int(),
  inTransitMinor: z.number().int(),
  holdingForOthersMinor: z.number().int(),
  reservedMinor: z.number().int(),
  spendableNowMinor: z.number().int(),
  netPositionMinor: z.number().int(),
  strictViolation: strictReserveViolationSchema, // WP-6.1 (GL-2 LOCKED)
  upcomingRecurringInflowMinor: z.number().int(),
  upcomingRecurringBillsMinor: z.number().int(),
  payableTotalMinor: z.number().int(),
  payableDueSoonMinor: z.number().int(),
  receivableTotalMinor: z.number().int(),
  receivableOverdueMinor: z.number().int(),
  period: z.object({ from: z.string(), to: z.string() }),
  grossIncomeMinor: z.number().int(),
  expensesMinor: z.number().int(),
  feesMinor: z.number().int(),
  netIncomeMinor: z.number().int(),
  netCashFlowMinor: z.number().int(),
});
export type DashboardResponse = z.infer<typeof dashboardResponseSchema>;

/** AC-5: consolidated cross-currency net worth, in `baseCurrency` — see
 * `Ledger.queryConsolidatedNetWorth`'s own doc comment for why a currency with no cached FX
 * rate contributes `convertedMinor: null` rather than a guessed figure. */
export const consolidatedNetWorthResponseSchema = z.object({
  baseCurrency: currencyCodeSchema,
  totalMinor: z.number().int(),
  perCurrency: z.array(
    z.object({
      currency: currencyCodeSchema,
      netPositionMinor: z.number().int(),
      convertedMinor: z.number().int().nullable(),
      rate: z.number().nullable(),
    }),
  ),
});
export type ConsolidatedNetWorthResponse = z.infer<typeof consolidatedNetWorthResponseSchema>;

// --- WP-1.12 search contracts (SR-1, S-37) ------------------------------------------------

const searchResultKindSchema = z.enum(SEARCH_RESULT_KINDS);

export const searchResultSchema = z.object({
  kind: searchResultKindSchema,
  id: z.string(),
  title: z.string(),
  subtitle: z.string().optional(),
  amountMinor: z.number().int().optional(),
  currency: currencyCodeSchema.optional(),
  at: z.string(),
  score: z.number(),
});
export type SearchResult = z.infer<typeof searchResultSchema>;

export const searchResponseSchema = z.object({
  results: z.array(searchResultSchema),
});
export type SearchResponse = z.infer<typeof searchResponseSchema>;

// --- WP-1.13 CSV import contracts (OB-2/3, TX-6, S-38 `/import`) --------------------------

// A single "signed amount" column (negative = expense, positive = income) — separate
// debit/credit columns are a deliberate first-slice scope boundary, not an oversight.
export const importColumnMappingSchema = z.object({
  date: z.string().min(1),
  amount: z.string().min(1),
  category: z.string().min(1).optional(),
  party: z.string().min(1).optional(),
  note: z.string().min(1).optional(),
});
export type ImportColumnMapping = z.infer<typeof importColumnMappingSchema>;

export const importSourceCreateRequestSchema = z.object({
  name: z.string().min(1).max(120),
  mapping: importColumnMappingSchema,
});
export type ImportSourceCreateRequest = z.infer<typeof importSourceCreateRequestSchema>;

export const importSourceEditRequestSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  mapping: importColumnMappingSchema.optional(),
});
export type ImportSourceEditRequest = z.infer<typeof importSourceEditRequestSchema>;

export const importSourceResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  mapping: importColumnMappingSchema,
  createdAt: z.string(),
});
export type ImportSourceResponse = z.infer<typeof importSourceResponseSchema>;

export const importSourceListResponseSchema = z.object({
  sources: z.array(importSourceResponseSchema),
});
export type ImportSourceListResponse = z.infer<typeof importSourceListResponseSchema>;

export const importPreviewRequestSchema = z.object({
  accountId: z.string().min(1),
  mapping: importColumnMappingSchema,
  rows: z.array(z.record(z.string(), z.string())).min(1).max(5_000),
});
export type ImportPreviewRequest = z.infer<typeof importPreviewRequestSchema>;

export const importPreviewRowSchema = z.discriminatedUnion('ok', [
  z.object({ rowIndex: z.number().int(), ok: z.literal(false), error: z.string() }),
  z.object({
    rowIndex: z.number().int(),
    ok: z.literal(true),
    type: transactionTypeSchema,
    amountMinor: z.number().int().positive(),
    at: z.string(),
    category: z.string().optional(),
    party: z.string().optional(),
    note: z.string().optional(),
    duplicateOfTransactionId: z.string().optional(), // TX-6: flagged, never blocked
  }),
]);
export type ImportPreviewRow = z.infer<typeof importPreviewRowSchema>;

export const importPreviewResponseSchema = z.object({
  rows: z.array(importPreviewRowSchema),
});
export type ImportPreviewResponse = z.infer<typeof importPreviewResponseSchema>;

const importCommitRowSchema = z.object({
  type: transactionTypeSchema,
  amountMinor: z.number().int().positive(),
  at: z.string(),
  category: z.string().optional(),
  party: z.string().optional(),
  note: z.string().optional(),
});

export const importCommitRequestSchema = z.object({
  accountId: z.string().min(1),
  /** OB-3: entering old history — the future notification consumer this flag is for
   * (WP-1.17) doesn't exist yet; the flag is still recorded faithfully on each row. */
  backfill: z.boolean().optional(),
  rows: z.array(importCommitRowSchema).min(1).max(5_000),
});
export type ImportCommitRequest = z.infer<typeof importCommitRequestSchema>;

export const importCommitResponseSchema = z.object({
  created: z.number().int().nonnegative(),
  transactionIds: z.array(z.string()),
});
export type ImportCommitResponse = z.infer<typeof importCommitResponseSchema>;

// --- WP-1.14 reconciliation contracts (OB-4, TX-1 Adjustment, S-13 `/accounts/:id/reconcile`) --

export const reconcileCandidateSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('uncategorized'),
    transactionId: z.string(),
    amountMinor: z.number().int(),
    at: z.string(),
  }),
  z.object({
    kind: z.literal('duplicate'),
    transactionId: z.string(),
    duplicateOfTransactionId: z.string(),
    amountMinor: z.number().int(),
    at: z.string(),
  }),
  z.object({
    kind: z.literal('stale_in_transit'),
    transferId: z.string(),
    sentMinor: z.number().int(),
    initiatedAt: z.string(),
    daysStale: z.number().int(),
  }),
  z.object({
    kind: z.literal('day_gap'),
    sinceDate: z.string(),
    gapDays: z.number().int(),
  }),
]);
export type ReconcileCandidate = z.infer<typeof reconcileCandidateSchema>;

export const reconciliationResponseSchema = z.object({
  accountId: z.string(),
  currency: currencyCodeSchema,
  computedBalanceMinor: z.number().int(),
  statedBalanceMinor: z.number().int(),
  driftMinor: z.number().int(),
  candidates: z.array(reconcileCandidateSchema),
});
export type ReconciliationResponse = z.infer<typeof reconciliationResponseSchema>;

export const adjustmentCreateRequestSchema = z.object({
  amountMinor: z
    .number()
    .int()
    .refine((v) => v !== 0, 'Adjustment amount must not be zero'),
  reason: z.string().min(10, 'Give a reason (10+ characters) so the history stays useful'),
});
export type AdjustmentCreateRequest = z.infer<typeof adjustmentCreateRequestSchema>;

// --- WP-1.16 Data Health contracts (OB-3, TX-6, LG-4, S-30 `/health`) ---------------------

export const dataHealthRowSchema = z.object({
  entityType: z.enum(['transaction', 'transfer', 'account', 'rp', 'tpf']),
  entityId: z.string(),
  detail: z.string(),
});
export type DataHealthRow = z.infer<typeof dataHealthRowSchema>;

export const dataHealthSectionSchema = z.object({
  id: z.string(),
  title: z.string(),
  severity: z.enum(['error', 'warning']),
  rows: z.array(dataHealthRowSchema),
});
export type DataHealthSection = z.infer<typeof dataHealthSectionSchema>;

export const dataHealthReportSchema = z.object({
  ok: z.boolean(),
  invariantsPassing: z.boolean(),
  sections: z.array(dataHealthSectionSchema),
});
export type DataHealthReport = z.infer<typeof dataHealthReportSchema>;

/** WP-5.5 (OB-3/LG-4 "maintenance actions"): a full replay from the durable event log
 * (`Ledger.rebuild()`, AR-4's own determinism check), compared against the live projection —
 * a read-only integrity diagnostic, not a state-mutating swap (see the route's own header
 * comment for why). `matches: false` should never happen on correct code; the owner is told
 * to restart the app, which already does a full reload from the durable log at boot. */
export const rebuildProjectionsResponseSchema = z.object({
  eventCount: z.number().int().nonnegative(),
  matches: z.boolean(),
});
export type RebuildProjectionsResponse = z.infer<typeof rebuildProjectionsResponseSchema>;

// --- WP-1.17 alert/banner contracts (NT-B, OD-7, OB-2, S-30 banner) ------------------------

export const alertSchema = z.object({
  id: z.string(),
  kind: z.string(),
  severity: z.enum(['error', 'warning']),
  message: z.string(),
  createdAt: z.string(),
});
export type Alert = z.infer<typeof alertSchema>;

export const alertListResponseSchema = z.object({
  alerts: z.array(alertSchema),
});
export type AlertListResponse = z.infer<typeof alertListResponseSchema>;

/** OB-2: the backup cron reports success/failure here (guarded by the ops token). */
export const backupReportRequestSchema = z.object({
  ok: z.boolean(),
  detail: z.string().max(1000).optional(),
});
export type BackupReportRequest = z.infer<typeof backupReportRequestSchema>;

// --- WP-1.18 attachment + note contracts (SR-3/4, SE-2, S-10 Attachments/Notes) -----------

/** An entity a note/attachment hangs on ("transaction", "account", "rp", "tpf", …). Kept a
 * free string — major entities span P1's built ones and P3's parties/projects. */
const entityRefSchema = z.object({
  entityType: z.string().min(1).max(40),
  entityId: z.string().min(1),
});

export const attachmentMetaSchema = z.object({
  id: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  filename: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  createdAt: z.string(),
});
export type AttachmentMeta = z.infer<typeof attachmentMetaSchema>;

export const attachmentListResponseSchema = z.object({
  attachments: z.array(attachmentMetaSchema),
});
export type AttachmentListResponse = z.infer<typeof attachmentListResponseSchema>;

export const attachmentUploadRequestSchema = entityRefSchema.extend({
  filename: z.string().min(1).max(255),
  contentType: z.string().min(1).max(120),
  /** base64-encoded file bytes; size is enforced server-side against the configured cap. */
  dataBase64: z.string().min(1),
});
export type AttachmentUploadRequest = z.infer<typeof attachmentUploadRequestSchema>;

export const noteSchema = z.object({
  id: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  body: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Note = z.infer<typeof noteSchema>;

export const noteListResponseSchema = z.object({
  notes: z.array(noteSchema),
});
export type NoteListResponse = z.infer<typeof noteListResponseSchema>;

export const noteCreateRequestSchema = entityRefSchema.extend({
  body: z.string().min(1).max(10_000),
});
export type NoteCreateRequest = z.infer<typeof noteCreateRequestSchema>;

export const noteUpdateRequestSchema = z.object({
  body: z.string().min(1).max(10_000),
});
export type NoteUpdateRequest = z.infer<typeof noteUpdateRequestSchema>;

// --- WP-C1.8 (SR-2): tags attachable to parties/projects/quotations ------------------------
// Mirrors the note contracts' own entityRefSchema shape exactly — see entityRefSchema's own
// doc comment. `tagIds` reference real tags (state.tags); existence is checked server-side.
export const entityTagListResponseSchema = z.object({
  tagIds: z.array(z.string()),
});
export type EntityTagListResponse = z.infer<typeof entityTagListResponseSchema>;

export const entityTagSetRequestSchema = entityRefSchema.extend({
  tagIds: z.array(z.string().min(1)),
});
export type EntityTagSetRequest = z.infer<typeof entityTagSetRequestSchema>;

// --- WP-1.19/1.20 settings contracts (ST-1, OB-1, S-04/S-39..46) --------------------------

/** The whole key→value settings bag. Values are heterogeneous (bool/string/number/object),
 * so a plain record — typed keys land in the Settings UI (WP-1.20) rather than the wire. */
export const settingsResponseSchema = z.object({
  settings: z.record(z.string(), z.unknown()),
});
export type SettingsResponse = z.infer<typeof settingsResponseSchema>;

export const settingUpdateRequestSchema = z.object({
  value: z.unknown(),
});
export type SettingUpdateRequest = z.infer<typeof settingUpdateRequestSchema>;

export const settingUpdateResponseSchema = z.object({
  key: z.string(),
  value: z.unknown(),
});
export type SettingUpdateResponse = z.infer<typeof settingUpdateResponseSchema>;

// --- WP-1.20 About error-log contract (S-46, surfaces the WP-0.5 persisted error log) -----

export const errorLogEntrySchema = z.object({
  id: z.number().int(),
  at: z.string(),
  level: z.string(),
  method: z.string().nullable(),
  url: z.string().nullable(),
  message: z.string(),
});
export type ErrorLogEntry = z.infer<typeof errorLogEntrySchema>;

export const errorLogResponseSchema = z.object({
  errors: z.array(errorLogEntrySchema),
});
export type ErrorLogResponse = z.infer<typeof errorLogResponseSchema>;

// --- WP-2.1 bridge transport contracts (§15.4 WB-1..9, SE-4, §9.4) ------------------------
// The bridge is a dumb pipe (ST-A/OP-2: transport-only, zero financial logic): it forwards
// EVERY inbound message with a stable id (WB-9) and executes the api's directive. All policy
// — designated-chat scope (WB-2), sender allowlist (SE-4), refuse-once memory (WA-2), replay
// dedupe (§9.4) — lives server-side, so the WP-7.7 Cloud-API fallback is a pure transport swap.

export const WA_MESSAGE_TYPES = ['text', 'voice', 'image', 'other'] as const;
export type WaMessageType = (typeof WA_MESSAGE_TYPES)[number];

export const bridgeInboundRequestSchema = z.object({
  /** WB-9: the stable WhatsApp message id — the backend dedupes reconnect replays on it. */
  messageId: z.string().min(1),
  chatJid: z.string().min(1),
  senderJid: z.string().min(1),
  /** WhatsApp's LID migration: the phone-number jid variant, present when the chat/participant
   * above is addressed by an opaque `@lid` instead of `@s.whatsapp.net` — the allowlist (SE-4)
   * is phone numbers, so this is what the backend actually matches against when set. */
  senderPnJid: z.string().min(1).optional(),
  at: z.string().min(1),
  type: z.enum(WA_MESSAGE_TYPES),
  text: z.string().max(10_000).optional(),
  /** WP-2.2 (WB-7): present when type is 'voice'/'image' — the bridge downloads the media and
   * forwards it base64-encoded in the same request; retained encrypted on the api side. */
  media: z.object({ mimeType: z.string().min(1), base64: z.string().min(1) }).optional(),
  // WP-F6 (CF-15, SE-6, WB-10): provenance. Baileys exposes all three; nothing carried them, so
  // the API could not tell a message the owner TYPED from one they FORWARDED from a client.
  //
  // All OPTIONAL, deliberately. The bridge deploys separately from the API, so requiring them
  // would reject every message from an older bridge — an outage, not a security improvement.
  // Absent is read as "unknown provenance", and the instruction slot is denied on that basis
  // wherever it matters.
  /** True when WhatsApp marked this a forwarded message: someone else's words, not the owner's. */
  forwarded: z.boolean().optional(),
  /** The message being replied to. Capped hard: it is text the OWNER DID NOT WRITE, so it is a
   * payload, not an instruction, and it has no business being 10KB. */
  quotedText: z.string().max(4_000).optional(),
  quotedMessageId: z.string().min(1).optional(),
});
export type BridgeInboundRequest = z.infer<typeof bridgeInboundRequestSchema>;

export const bridgeInboundResponseSchema = z.object({
  duplicate: z.boolean(), // §9.4/WB-9: true ⇒ replay; the bridge does nothing further
  action: z.enum(['accept', 'ignore', 'refuse']),
  /** Present only for 'refuse' — the §18.4 verbatim unknown-sender copy, sent once (WA-2). */
  replyText: z.string().optional(),
});
export type BridgeInboundResponse = z.infer<typeof bridgeInboundResponseSchema>;

export const BRIDGE_STATES = ['connecting', 'open', 'close', 'logged_out'] as const;
export const bridgeStatusRequestSchema = z.object({
  state: z.enum(BRIDGE_STATES),
  /** The Baileys pairing QR payload while awaiting a scan (WB-4 re-link flow); null clears it. */
  qr: z.string().nullable().optional(),
});
export type BridgeStatusRequest = z.infer<typeof bridgeStatusRequestSchema>;

// WP-7.7 (§9.2, WB-8): owner-initiated pushes (reminders/alerts) MUST use a pre-approved
// template outside an active session window — this is the shape both transports (Baileys and
// the Cloud-API fallback) agree on for a templated send.
export const waTemplateSchema = z.object({
  name: z.string(),
  languageCode: z.string(),
  params: z.array(z.string()),
});
export type WaTemplate = z.infer<typeof waTemplateSchema>;

export const bridgeOutboxResponseSchema = z.object({
  messages: z.array(
    z.object({
      id: z.string(),
      toJid: z.string(),
      text: z.string(),
      template: waTemplateSchema.optional(),
    }),
  ),
});
export type BridgeOutboxResponse = z.infer<typeof bridgeOutboxResponseSchema>;

export const bridgeOutboxAckRequestSchema = z.object({ id: z.string().min(1) });
export type BridgeOutboxAckRequest = z.infer<typeof bridgeOutboxAckRequestSchema>;

/** WB-3: an opaque, bridge-side-serialized auth-state snapshot; the api encrypts it at rest
 * so the nightly pg_dump covers it and a redeploy never forces a QR re-scan. */
export const bridgeCredsPutRequestSchema = z.object({ data: z.string().min(1).max(5_000_000) });
export type BridgeCredsPutRequest = z.infer<typeof bridgeCredsPutRequestSchema>;

export const bridgeCredsResponseSchema = z.object({ data: z.string().nullable() });
export type BridgeCredsResponse = z.infer<typeof bridgeCredsResponseSchema>;

// --- Capture Inbox (WP-2.5, M23 IN-1..5) ------------------------------------------------

export const CAPTURE_SOURCES = ['whatsapp', 'web', 'import'] as const;
export type CaptureSource = (typeof CAPTURE_SOURCES)[number];

export const CAPTURE_KINDS = ['voice', 'text', 'image'] as const;
export type CaptureKind = (typeof CAPTURE_KINDS)[number];

/** IN-1's canonical 5-value status enum (display labels/grouping live in the web layer). */
export const CAPTURE_STATUSES = [
  'needs_one_tap',
  'needs_question',
  'auto_recorded',
  'unparsed_failed',
  'duplicate_suspect',
] as const;
export type CaptureStatus = (typeof CAPTURE_STATUSES)[number];

export const CAPTURE_CONFIDENCE_LEVELS = ['high', 'medium', 'low'] as const;
export type CaptureConfidenceLevel = (typeof CAPTURE_CONFIDENCE_LEVELS)[number];

export const CAPTURE_MODES = ['A', 'B', 'C'] as const;
export type CaptureMode = (typeof CAPTURE_MODES)[number];

export const CAPTURE_EVENT_TYPES = ['expense', 'income', 'transfer', 'settlement', 'tpf'] as const;
export type CaptureEventType = (typeof CAPTURE_EVENT_TYPES)[number];

// WP-4.1 (AU-3): the three cross-event linking rules — "a fee attaches to the payment in the
// same utterance; a transfer following a receipt links source account to the receipt's
// account; an expense after a transfer defaults to the destination account."
export const CAPTURE_EVENT_RELATIONS = ['fee', 'transferSourceFrom', 'expenseAccountFrom'] as const;
export type CaptureEventRelation = (typeof CAPTURE_EVENT_RELATIONS)[number];

export const captureEventLinkSchema = z.object({
  targetIndex: z.number().int().nonnegative(),
  relation: z.enum(CAPTURE_EVENT_RELATIONS),
});
export type CaptureEventLink = z.infer<typeof captureEventLinkSchema>;

const captureCandidateRefSchema = z.object({ id: z.string(), label: z.string() });

/** Display-ready — labels alongside ids, a snapshot at capture time (WP-2.4's raw
 * candidateId-only shape gets enriched with the matcher's Candidate.label before storage).
 * `toAccount`/`feeType`/`linkedTo` are WP-4.1 additions, truly optional (never set by any
 * pre-WP-4.1 single-event extraction) — `toAccount` is a transfer's destination account
 * (`account` is the source/from side); `linkedTo` is set only on an entry inside a multi-event
 * `CaptureItemResponse.extractions[]` array that attaches to, or defaults a field from, an
 * earlier entry in the SAME array (AU-3). */
export const captureExtractionSchema = z.object({
  eventType: z.enum(CAPTURE_EVENT_TYPES).nullable(),
  amountMinor: z.number().nullable(),
  currency: z.string().nullable(),
  explicitCurrency: z.boolean(),
  account: captureCandidateRefSchema.nullable(),
  toAccount: captureCandidateRefSchema.nullable().optional(),
  category: captureCandidateRefSchema.nullable(),
  /** PT-6: a provisional/new party carries only a label, no id yet. */
  party: z.union([captureCandidateRefSchema, z.object({ label: z.string() })]).nullable(),
  date: z.string().nullable(),
  feeMinor: z.number().nullable(),
  feeType: feeTypeSchema.nullable().optional(),
  purpose: z.string().nullable(),
  linkedTo: captureEventLinkSchema.nullable().optional(),
});
export type CaptureExtraction = z.infer<typeof captureExtractionSchema>;

export const captureResultLinkSchema = z.object({ entityType: z.string(), entityId: z.string() });
export type CaptureResultLink = z.infer<typeof captureResultLinkSchema>;

export const captureItemSchema = z.object({
  id: z.string(),
  source: z.enum(CAPTURE_SOURCES),
  kind: z.enum(CAPTURE_KINDS),
  status: z.enum(CAPTURE_STATUSES),
  rawText: z.string().nullable(),
  lowConfidence: z.boolean(),
  mediaId: z.string().nullable(),
  /** S-08: same-origin, auth-gated (SE-2) URL to the decrypted voice-note/image bytes —
   * resolved from mediaId via WP-2.2's attachment store, null when there's no media or no
   * attachment key configured. */
  mediaContentUrl: z.string().nullable(),
  extraction: captureExtractionSchema.nullable(),
  /** WP-4.1 (AU-2, S-08): >1 entry means "the AI found several linked events" — the grouped
   * card. Null for every single-event item (the overwhelmingly common case, unchanged). */
  extractions: z.array(captureExtractionSchema).nullable(),
  confidence: z.enum(CAPTURE_CONFIDENCE_LEVELS).nullable(),
  mode: z.enum(CAPTURE_MODES).nullable(),
  unparsedReason: z.string().nullable(),
  resultEntityType: z.string().nullable(),
  resultEntityId: z.string().nullable(),
  /** WP-4.1: index-aligned with `extractions` once "Record all" has run — a `fee`-linked
   * entry's link duplicates its target's, since a fee is folded into the target's own
   * transaction rather than recorded as a separate one (IN-4: "each created record links
   * back here", read per underlying record, not per displayed line). */
  resultLinks: z.array(captureResultLinkSchema).nullable(),
  /** WP-4.3 (AU-6/TX-6, S-08): set only on a `duplicate_suspect` item — the existing
   * transaction it suspects it duplicates (the "twin" S-08's own wireframe shows inline). */
  duplicateOfTransactionId: z.string().nullable(),
  createdAt: z.string(),
  resolvedAt: z.string().nullable(),
});
export type CaptureItemResponse = z.infer<typeof captureItemSchema>;

/** S-07: grouped by status, plus IN-5's badge count (actionable = everything except
 * auto_recorded — surfaced as a resolved reading of an otherwise-unspecified spec term). */
export const captureInboxListResponseSchema = z.object({
  items: z.array(captureItemSchema),
  badgeCount: z.number().int().nonnegative(),
});
export type CaptureInboxListResponse = z.infer<typeof captureInboxListResponseSchema>;

export const captureBulkActionRequestSchema = z.object({
  ids: z.array(z.string().min(1)).min(1).max(100),
  action: z.enum(['record', 'reject']),
});
export type CaptureBulkActionRequest = z.infer<typeof captureBulkActionRequestSchema>;

export const captureBulkActionResponseSchema = z.object({
  results: z.array(z.object({ id: z.string(), ok: z.boolean(), error: z.string().optional() })),
});
export type CaptureBulkActionResponse = z.infer<typeof captureBulkActionResponseSchema>;

export const captureRecordResponseSchema = z.object({ transactionId: z.string() });
export type CaptureRecordResponse = z.infer<typeof captureRecordResponseSchema>;

/** WP-4.1 (AU-2, IN-4): "Record all" is all-or-nothing (validated up front, see
 * inboxService.ts) — on success, one result per `extractions[]` entry, index-aligned; a
 * `fee`-linked entry's result duplicates its target's (see `resultLinks` above). */
export const captureRecordAllResponseSchema = z.object({
  results: z.array(
    z.object({
      index: z.number().int().nonnegative(),
      entityType: z.string(),
      entityId: z.string(),
    }),
  ),
});
export type CaptureRecordAllResponse = z.infer<typeof captureRecordAllResponseSchema>;

// --- Web AI chat capture (WP-2.6, M24 AI-1..3, S-06) ------------------------------------

/** AI-1: text capture — "identical pipeline as WhatsApp" (AR-3), just returned inline
 * instead of via the WhatsApp outbox. `mediaId`/`lowConfidence` are set only when this text
 * is an owner-corrected voice transcript (AI-3), to preserve provenance on the resulting item. */
export const chatCaptureRequestSchema = z.object({
  text: z.string().min(1).max(2_000),
  mediaId: z.string().min(1).optional(),
  lowConfidence: z.boolean().optional(),
});
export type ChatCaptureRequest = z.infer<typeof chatCaptureRequestSchema>;

export const chatCaptureResponseSchema = z.object({
  itemId: z.string().nullable(),
  mode: z.enum(CAPTURE_MODES).nullable(),
  reply: z.string(),
  /** The full Capture Item, so the proposal card can render entity chips + Confirm/Adjust/
   * Reject without a second round trip. Null only when the reply itself created no item
   * (e.g. "undo" with nothing pending). */
  item: captureItemSchema.nullable(),
});
export type ChatCaptureResponse = z.infer<typeof chatCaptureResponseSchema>;

/** AI-3: STT runs synchronously here (the web caller is waiting on the HTTP response,
 * unlike the WhatsApp bridge's async post-STT job) — a low-confidence transcript pauses for
 * review BEFORE the capture pipeline ever sees a guessed transcript, instead of guessing. */
export const chatVoiceCaptureRequestSchema = z.object({
  mimeType: z.string().min(1),
  base64: z.string().min(1),
});
export type ChatVoiceCaptureRequest = z.infer<typeof chatVoiceCaptureRequestSchema>;

export const chatVoiceCaptureResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('needs_review'), mediaId: z.string(), transcript: z.string() }),
  z.object({
    status: z.literal('processed'),
    mediaId: z.string(),
    transcript: z.string(),
    itemId: z.string().nullable(),
    mode: z.enum(CAPTURE_MODES).nullable(),
    reply: z.string(),
    item: captureItemSchema.nullable(),
  }),
  z.object({ status: z.literal('failed'), mediaId: z.string().nullable(), reason: z.string() }),
]);
export type ChatVoiceCaptureResponse = z.infer<typeof chatVoiceCaptureResponseSchema>;

// --- AI usage / budget meter (WP-2.8, CA-6, CN-2, OB-1) ----------------------------------

export const AI_REQUEST_KINDS = ['stt', 'llm'] as const;
export type AiRequestKind = (typeof AI_REQUEST_KINDS)[number];

export const aiUsageRecordSchema = z.object({
  id: z.string(),
  kind: z.enum(AI_REQUEST_KINDS),
  provider: z.string(),
  costCents: z.number().int().nonnegative(),
  at: z.string(),
});
export type AiUsageRecord = z.infer<typeof aiUsageRecordSchema>;

/** S-39..46 "AI" settings section: budget cap + this-month meter + a basic activity-log
 * viewer (recent, most-first). The cap itself is read/written through the existing generic
 * settings routes (`aiMonthlyCapCents`) — this endpoint is read-only aggregation. */
export const aiUsageResponseSchema = z.object({
  spentCents: z.number().int().nonnegative(),
  capCents: z.number().int().positive(),
  recent: z.array(aiUsageRecordSchema),
});
export type AiUsageResponse = z.infer<typeof aiUsageResponseSchema>;

// --- Calendar connections (WP-C1.1, INT-1/2/4, SB-4, CAL-4) -----------------------------

/** SB-4: "shown once, stored encrypted" — the secret feed URL is submitted here but never
 * echoed back by any response schema below, including this create response. */
export const calendarConnectionCreateRequestSchema = z.object({
  label: z.string().min(1).max(100),
  icsUrl: z.url().max(2000),
  color: z.string().min(1).max(20).optional(),
  includeInAvailability: z.boolean().optional(),
  reminderLeadMinutes: z.number().int().nonnegative().optional(),
});
export type CalendarConnectionCreateRequest = z.infer<typeof calendarConnectionCreateRequestSchema>;

export const calendarConnectionEditRequestSchema = z.object({
  label: z.string().min(1).max(100).optional(),
  color: z.string().min(1).max(20).nullable().optional(),
  includeInAvailability: z.boolean().optional(),
  reminderLeadMinutes: z.number().int().nonnegative().optional(),
});
export type CalendarConnectionEditRequest = z.infer<typeof calendarConnectionEditRequestSchema>;

export const calendarConnectionSchema = z.object({
  id: z.string(),
  label: z.string(),
  color: z.string().nullable(),
  includeInAvailability: z.boolean(),
  reminderLeadMinutes: z.number().int(),
  /** WP-C2.1/C2.2 (INT-3): 'ics' (Track C1, the default — older clients/fixtures omitting this
   * field still parse) | 'google' | 'microsoft' (Track C2, delegated OAuth). */
  provider: z.enum(['ics', 'google', 'microsoft']).default('ics'),
  /** INT-2: "shows 'last refreshed' per calendar" — null only for a connection whose very
   * first fetch (at creation) failed. */
  lastRefreshedAt: z.string().nullable(),
  lastRefreshError: z.string().nullable(),
  createdAt: z.string(),
});
export type CalendarConnectionResponse = z.infer<typeof calendarConnectionSchema>;

export const calendarConnectionListResponseSchema = z.object({
  connections: z.array(calendarConnectionSchema),
});
export type CalendarConnectionListResponse = z.infer<typeof calendarConnectionListResponseSchema>;

// --- Calendar OAuth (WP-C2.1/C2.2, INT-3, SB-4) -----------------------------------------

export const CALENDAR_OAUTH_PROVIDERS = ['google', 'microsoft'] as const;
export type CalendarOAuthProvider = (typeof CALENDAR_OAUTH_PROVIDERS)[number];

/** GET .../oauth/:provider/start response — the SPA does `window.location.assign(authorizationUrl)`
 * itself rather than following a server-side redirect (this step is a JSON API call). */
export const calendarOAuthStartResponseSchema = z.object({ authorizationUrl: z.string() });
export type CalendarOAuthStartResponse = z.infer<typeof calendarOAuthStartResponseSchema>;

// --- Calendar event write (WP-C2.3, CW-1..3, SB-1) --------------------------------------

/** CW-1: "title, date/time + zone, duration, target calendar, optional note" — shared by
 * both the conflict-preview and the create request. CW-3: deliberately NO attendees/location/
 * video-link fields — explicitly out of scope for C2 v1 (the existing read-side
 * `agendaEventSchema.location` is unaffected; it simply isn't settable here). */
export const calendarEventWriteInputSchema = z.object({
  title: z.string().min(1).max(200),
  startAt: z.string().min(1),
  endAt: z.string().min(1),
  timeZone: z.string().min(1),
  note: z.string().max(2000).optional(),
});
export type CalendarEventWriteInput = z.infer<typeof calendarEventWriteInputSchema>;

/** CW-2: edit/move — every field optional (a partial patch); only fields present are changed. */
export const calendarEventUpdateRequestSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  startAt: z.string().min(1).optional(),
  endAt: z.string().min(1).optional(),
  timeZone: z.string().min(1).optional(),
  note: z.string().max(2000).optional(),
});
export type CalendarEventUpdateRequest = z.infer<typeof calendarEventUpdateRequestSchema>;

/** SB-1: "deletion double-confirmed" — the caller must echo the event's exact title back;
 * validated server-side (oauthConnectionService.deleteEvent) against the real stored title,
 * never trusted from a client-side check alone. */
export const calendarEventDeleteRequestSchema = z.object({ confirmTitle: z.string().min(1) });
export type CalendarEventDeleteRequest = z.infer<typeof calendarEventDeleteRequestSchema>;

export const calendarEventDeleteResponseSchema = z.object({ ok: z.literal(true) });
export type CalendarEventDeleteResponse = z.infer<typeof calendarEventDeleteResponseSchema>;

/** The write-side event shape returned by preview/create/update — mirrors
 * apps/api/src/calendar/eventModel.ts's `CalendarEvent` exactly (no calendarLabel/calendarColor
 * display fields; those are agendaEventSchema's own concern, not this one). */
export const calendarEventSchema = z.object({
  id: z.string(),
  calendarConnectionId: z.string(),
  uid: z.string(),
  title: z.string(),
  startAt: z.string(),
  endAt: z.string(),
  allDay: z.boolean(),
  location: z.string().nullable(),
});
export type CalendarEventResponse = z.infer<typeof calendarEventSchema>;

/** CW-1: "conflicts are warned before confirm" — POST .../events/preview's response. */
export const calendarEventPreviewResponseSchema = z.object({
  conflicts: z.array(calendarEventSchema),
});
export type CalendarEventPreviewResponse = z.infer<typeof calendarEventPreviewResponseSchema>;

// --- Agenda (WP-C1.2, CAL-1..5, S-34) ----------------------------------------------------

export const agendaEventSchema = z.object({
  id: z.string(),
  calendarConnectionId: z.string(),
  calendarLabel: z.string(),
  calendarColor: z.string().nullable(),
  uid: z.string(),
  title: z.string(),
  startAt: z.string(),
  endAt: z.string(),
  allDay: z.boolean(),
  location: z.string().nullable(),
});
export type AgendaEventResponse = z.infer<typeof agendaEventSchema>;

export const AGENDA_FINANCE_CHIP_KINDS = ['payable_due', 'recurring_due', 'schedule_due'] as const;
export type AgendaFinanceChipKind = (typeof AGENDA_FINANCE_CHIP_KINDS)[number];

/** CAL-3 (MAY): payment due dates / recurring occurrences / schedule items, read-only,
 * overlaid on the merged agenda. */
export const agendaFinanceChipSchema = z.object({
  kind: z.enum(AGENDA_FINANCE_CHIP_KINDS),
  label: z.string(),
  amountLabel: z.string().nullable(),
  at: z.string(),
  entityId: z.string(),
});
export type AgendaFinanceChip = z.infer<typeof agendaFinanceChipSchema>;

export const agendaResponseSchema = z.object({
  events: z.array(agendaEventSchema),
  financeChips: z.array(agendaFinanceChipSchema),
});
export type AgendaResponse = z.infer<typeof agendaResponseSchema>;

// --- Availability (WP-C1.5, AVQ-1/2, TZ-4, S-35) ------------------------------------------

const availabilitySpanSchema = z.object({ start: z.string(), end: z.string() });

export const availabilityCheckRequestSchema = z.object({ text: z.string().min(1).max(500) });
export type AvailabilityCheckRequest = z.infer<typeof availabilityCheckRequestSchema>;

/** AVQ-1: a pasted "he's available..." message either resolves to a full answer, or — TZ-4's
 * "confirmed, not guessed" — comes back as a clarifying question (ambiguous zone/date, or
 * nothing parseable at all). */
export const availabilityCheckResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('question'), reply: z.string() }),
  z.object({
    status: z.literal('answer'),
    reply: z.string(),
    theirZone: z.string(),
    ownerZone: z.string(),
    theirRange: availabilitySpanSchema,
    ownerRange: availabilitySpanSchema,
    crossesMidnight: z.boolean(),
    conflicts: z.array(availabilitySpanSchema),
    freeBlocks: z.array(availabilitySpanSchema),
    suggestedSlot: availabilitySpanSchema.nullable(),
  }),
]);
export type AvailabilityCheckResponse = z.infer<typeof availabilityCheckResponseSchema>;

// --- Ad-hoc reminders (WP-C1.6, RM-2/3/4, S-36 `/reminders`) ------------------------------

export const reminderChannelSchema = z.enum(['whatsapp']);

export const reminderResponseSchema = z.object({
  id: z.string(),
  text: z.string(),
  channel: reminderChannelSchema,
  nextRunAt: z.string(),
  schedule: recurScheduleSchema.optional(),
  status: z.enum(['active', 'cancelled', 'done']),
  snoozed: z.boolean(),
  linkType: z.string().optional(),
  linkId: z.string().optional(),
  lastFiredAt: z.string().optional(),
  createdAt: z.string(),
});
export type ReminderResponse = z.infer<typeof reminderResponseSchema>;

export const reminderListResponseSchema = z.object({ reminders: z.array(reminderResponseSchema) });
export type ReminderListResponse = z.infer<typeof reminderListResponseSchema>;

/** S-36 "+ New": a structured create — `nextRunAt` is a resolved instant (a native datetime
 * picker on the web; the WhatsApp/chat NL path resolves "tomorrow 5pm" server-side before
 * ever reaching this shape, per RM-2's own worked example). */
export const reminderCreateRequestSchema = z.object({
  text: z.string().min(1).max(500),
  nextRunAt: z.string().min(1),
  schedule: recurScheduleSchema.optional(),
  linkType: z.string().min(1).max(40).optional(),
  linkId: z.string().min(1).max(120).optional(),
});
export type ReminderCreateRequest = z.infer<typeof reminderCreateRequestSchema>;

/** RM-3 "remind me again in 1 hour" — the web page's snooze presets send whole minutes. */
export const reminderSnoozeRequestSchema = z.object({ minutes: z.number().int().positive() });
export type ReminderSnoozeRequest = z.infer<typeof reminderSnoozeRequestSchema>;

// --- Parties (WP-3.1, M1 PT-1..6, S-19/S-20) -----------------------------------------------

export const partyRoleSchema = z.enum(PARTY_ROLES);

export const partyResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  roles: z.array(partyRoleSchema),
  aliases: z.array(z.string()),
  phone: z.string().optional(),
  archived: z.boolean(),
  provisional: z.boolean(),
  mergedIntoId: z.string().optional(),
  createdAt: z.string(),
});
export type PartyResponse = z.infer<typeof partyResponseSchema>;

export const partyListResponseSchema = z.object({ parties: z.array(partyResponseSchema) });
export type PartyListResponse = z.infer<typeof partyListResponseSchema>;

export const partyCreateRequestSchema = z.object({
  name: z.string().min(1).max(120),
  roles: z.array(partyRoleSchema).optional(),
  aliases: z.array(z.string().min(1).max(60)).optional(),
  phone: z.string().min(1).max(40).optional(),
  provisional: z.boolean().optional(),
});
export type PartyCreateRequest = z.infer<typeof partyCreateRequestSchema>;

export const partyEditRequestSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  roles: z.array(partyRoleSchema).optional(),
  aliases: z.array(z.string().min(1).max(60)).optional(),
  phone: z.string().min(1).max(40).optional(),
});
export type PartyEditRequest = z.infer<typeof partyEditRequestSchema>;

/** S-19 "field-by-field keep-picker" — the fields are the owner's chosen values for the
 * SURVIVING party; omitted fields keep whatever `keepId` already had. */
export const partyMergeRequestSchema = z.object({
  keepId: z.string().min(1),
  mergeId: z.string().min(1),
  name: z.string().min(1).max(120).optional(),
  roles: z.array(partyRoleSchema).optional(),
  aliases: z.array(z.string().min(1).max(60)).optional(),
  phone: z.string().min(1).max(40).optional(),
});
export type PartyMergeRequest = z.infer<typeof partyMergeRequestSchema>;

export const currencyAmountSchema = z.object({
  currency: currencyCodeSchema,
  amountMinor: z.number().int(),
});
export type CurrencyAmount = z.infer<typeof currencyAmountSchema>;

/** PT-2's profile stat strip + tab data. */
export const partyProfileResponseSchema = z.object({
  party: partyResponseSchema,
  receivedTotal: z.array(currencyAmountSchema),
  paidTotal: z.array(currencyAmountSchema),
  receivableMinor: z.array(currencyAmountSchema),
  payableMinor: z.array(currencyAmountSchema),
  tpfHeldMinor: z.array(currencyAmountSchema),
  transactionIds: z.array(z.string()),
  rpIds: z.array(z.string()),
  tpfCaseIds: z.array(z.string()),
  quotationIds: z.array(z.string()), // WP-3.2
  projectIds: z.array(z.string()), // WP-3.3
  scheduleItemIds: z.array(z.string()), // WP-3.4
});
export type PartyProfileResponse = z.infer<typeof partyProfileResponseSchema>;

// --- Quotations (WP-3.2, M2 QT-1..6, S-21/22) -----------------------------------------------

export const quotationStateSchema = z.enum(QUOTATION_STATES);
export const quotationConvertTargetSchema = z.enum(QUOTATION_CONVERT_TARGETS);

/** QT-3 (LOCKED): fixed vs range is a real distinction, never collapsed to one number. */
export const quotationPricingSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('fixed'), amountMinor: z.number().int().positive() }),
  z.object({
    kind: z.literal('range'),
    minMinor: z.number().int().positive(),
    maxMinor: z.number().int().positive(),
  }),
]);
export type QuotationPricing = z.infer<typeof quotationPricingSchema>;

export const quotationVersionSchema = z.object({
  pricing: quotationPricingSchema,
  supersededAt: z.string(),
});
export type QuotationVersion = z.infer<typeof quotationVersionSchema>;

export const quotationResponseSchema = z.object({
  id: z.string(),
  party: z.string(),
  partyId: z.string().optional(),
  title: z.string(),
  description: z.string().optional(),
  pricing: quotationPricingSchema,
  currency: currencyCodeSchema,
  expiryDate: z.string().optional(),
  notes: z.string().optional(),
  paymentTerms: z.string().optional(),
  expectedStartDate: z.string().optional(),
  status: quotationStateSchema,
  versions: z.array(quotationVersionSchema),
  convertedToType: quotationConvertTargetSchema.optional(),
  convertedToId: z.string().optional(),
  createdAt: z.string(),
});
export type QuotationResponse = z.infer<typeof quotationResponseSchema>;

export const quotationListResponseSchema = z.object({
  quotations: z.array(quotationResponseSchema),
});
export type QuotationListResponse = z.infer<typeof quotationListResponseSchema>;

/** QT-2's own field list. */
export const quotationCreateRequestSchema = z.object({
  party: z.string().min(1).max(120),
  partyId: z.string().min(1).optional(),
  title: z.string().min(1).max(200),
  description: z.string().min(1).max(2000).optional(),
  pricing: quotationPricingSchema,
  currency: currencyCodeSchema,
  expiryDate: z.string().min(1).optional(),
  notes: z.string().min(1).max(1000).optional(),
  paymentTerms: z.string().min(1).max(500).optional(),
  expectedStartDate: z.string().min(1).optional(),
});
export type QuotationCreateRequest = z.infer<typeof quotationCreateRequestSchema>;

/** Non-price fields only — pricing revisions go through their own request (QT-5). */
export const quotationEditRequestSchema = z.object({
  party: z.string().min(1).max(120).optional(),
  partyId: z.string().min(1).optional(),
  title: z.string().min(1).max(200).optional(),
  description: z.string().min(1).max(2000).optional(),
  expiryDate: z.string().min(1).optional(),
  notes: z.string().min(1).max(1000).optional(),
  paymentTerms: z.string().min(1).max(500).optional(),
  expectedStartDate: z.string().min(1).optional(),
});
export type QuotationEditRequest = z.infer<typeof quotationEditRequestSchema>;

export const quotationRevisePricingRequestSchema = z.object({ pricing: quotationPricingSchema });
export type QuotationRevisePricingRequest = z.infer<typeof quotationRevisePricingRequestSchema>;

/** QT-4: `amountMinor` is required for a range-priced quotation converting to a receivable
 * (QT-3: never silently collapsed to a single number); optional override for a fixed one. */
export const quotationConvertRequestSchema = z.object({
  target: quotationConvertTargetSchema,
  amountMinor: z.number().int().positive().optional(),
});
export type QuotationConvertRequest = z.infer<typeof quotationConvertRequestSchema>;

// --- Projects (WP-3.3, M3 PJ-1..7, S-23/24) -------------------------------------------------

export const projectEarningModelSchema = z.enum(PROJECT_EARNING_MODELS);
export const projectStatusSchema = z.enum(PROJECT_STATUSES);
export const projectAdjustmentTypeSchema = z.enum(PROJECT_ADJUSTMENT_TYPES);

const projectQuotedRangeSchema = z.object({
  minMinor: z.number().int().positive(),
  maxMinor: z.number().int().positive(),
});

export const projectAdjustmentSchema = z.object({
  id: z.string(),
  type: projectAdjustmentTypeSchema,
  amountMinor: z.number().int(), // signed
  date: z.string(),
  note: z.string().optional(),
});
export type ProjectAdjustment = z.infer<typeof projectAdjustmentSchema>;

export const projectHoursEntrySchema = z.object({
  id: z.string(),
  date: z.string(),
  hours: z.number().positive(),
  rateMinor: z.number().int().positive(),
  note: z.string().optional(),
  adjustmentId: z.string(),
});
export type ProjectHoursEntry = z.infer<typeof projectHoursEntrySchema>;

export const projectResponseSchema = z.object({
  id: z.string(),
  party: z.string(),
  partyId: z.string().optional(),
  title: z.string(),
  serviceCategory: z.string().optional(),
  productCategory: z.string().optional(),
  currency: currencyCodeSchema,
  status: projectStatusSchema,
  earningModel: projectEarningModelSchema,
  originalQuotedRange: projectQuotedRangeSchema.optional(),
  agreedMinor: z.number().int().positive(),
  adjustments: z.array(projectAdjustmentSchema),
  hoursEntries: z.array(projectHoursEntrySchema),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  notes: z.string().optional(),
  fromQuotationId: z.string().optional(),
  // WP-3.6 (S-23 row fields) — Final-Value basis, server-computed so the list never fans out
  // into one profitability request per row; the interactive per-basis switch lives on S-24.
  receivedPercent: z.number().optional(), // grossReceived / finalValue; absent when finalValue <= 0
  marginPercent: z.number().optional(), // PP-1/PP-2, Final Value basis; absent when finalValue <= 0
  createdAt: z.string(),
});
export type ProjectResponse = z.infer<typeof projectResponseSchema>;

export const projectListResponseSchema = z.object({ projects: z.array(projectResponseSchema) });
export type ProjectListResponse = z.infer<typeof projectListResponseSchema>;

/** PJ-4's own field list; `agreedMinor` is PJ-3's "original agreement" — set once here, never
 * editable again. */
export const projectCreateRequestSchema = z.object({
  party: z.string().min(1).max(120),
  partyId: z.string().min(1).optional(),
  title: z.string().min(1).max(200),
  serviceCategory: z.string().min(1).max(120).optional(),
  productCategory: z.string().min(1).max(120).optional(),
  currency: currencyCodeSchema,
  earningModel: projectEarningModelSchema,
  originalQuotedRange: projectQuotedRangeSchema.optional(),
  agreedMinor: z.number().int().positive(),
  startDate: z.string().min(1).optional(),
  endDate: z.string().min(1).optional(),
  notes: z.string().min(1).max(1000).optional(),
  /** QT-4 "from-quotation prefill" — zero re-entry. */
  fromQuotationId: z.string().min(1).optional(),
});
export type ProjectCreateRequest = z.infer<typeof projectCreateRequestSchema>;

export const projectEditRequestSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  serviceCategory: z.string().min(1).max(120).optional(),
  productCategory: z.string().min(1).max(120).optional(),
  status: projectStatusSchema.optional(),
  startDate: z.string().min(1).optional(),
  endDate: z.string().min(1).optional(),
  notes: z.string().min(1).max(1000).optional(),
});
export type ProjectEditRequest = z.infer<typeof projectEditRequestSchema>;

/** PJ-3's own typed ledger — a note is required for `discount`/`compensation` (enforced by
 * the engine; this schema stays permissive so the 422 reads as a domain error, not a shape
 * error). */
export const projectAddAdjustmentRequestSchema = z.object({
  type: projectAdjustmentTypeSchema,
  amountMinor: z.number().int(),
  date: z.string().min(1),
  note: z.string().min(1).max(1000).optional(),
});
export type ProjectAddAdjustmentRequest = z.infer<typeof projectAddAdjustmentRequestSchema>;

/** PJ-5: hourly projects only. */
export const projectLogHoursRequestSchema = z.object({
  date: z.string().min(1),
  hours: z.number().positive(),
  rateMinor: z.number().int().positive(),
  note: z.string().min(1).max(1000).optional(),
});
export type ProjectLogHoursRequest = z.infer<typeof projectLogHoursRequestSchema>;

/** MI-3's four lenses. */
export const projectValueLensesResponseSchema = z.object({
  originalQuotedRange: projectQuotedRangeSchema.optional(),
  agreedMinor: z.number().int(),
  finalValueMinor: z.number().int(),
  grossReceivedMinor: z.number().int(),
  netUsableMinor: z.number().int(),
});
export type ProjectValueLensesResponse = z.infer<typeof projectValueLensesResponseSchema>;

/** PJ-7 "unallocated then relink". */
export const relinkTransactionRequestSchema = z.object({ projectId: z.string().min(1) });
export type RelinkTransactionRequest = z.infer<typeof relinkTransactionRequestSchema>;

// --- Schedule items (WP-3.4, M4 SC-1..8, S-14) ----------------------------------------------

export const confidenceLevelSchema = z.enum(CONFIDENCE_LEVELS);
export const scheduleItemStatusSchema = z.enum(SCHEDULE_ITEM_STATUSES);

export const scheduleItemReceiptSchema = z.object({
  amountMinor: z.number().int(),
  transactionId: z.string(),
  at: z.string(),
});
export type ScheduleItemReceipt = z.infer<typeof scheduleItemReceiptSchema>;

export const scheduleRescheduleSchema = z.object({
  previousDate: z.string().optional(),
  newDate: z.string().optional(),
  at: z.string(),
});
export type ScheduleReschedule = z.infer<typeof scheduleRescheduleSchema>;

export const scheduleItemResponseSchema = z.object({
  id: z.string(),
  party: z.string(),
  partyId: z.string().optional(),
  projectId: z.string().optional(),
  currency: currencyCodeSchema,
  expectedAmountMinor: z.number().int().positive(),
  expectedDate: z.string().optional(),
  milestone: z.string().optional(),
  confidence: confidenceLevelSchema,
  /** SC-2's own full 6-value enum — the response always carries the QUERY-TIME effective
   * status (due/overdue included), never the engine's narrower stored subset. */
  status: scheduleItemStatusSchema,
  receipts: z.array(scheduleItemReceiptSchema),
  receivedMinor: z.number().int(),
  remainingMinor: z.number().int(),
  rescheduleHistory: z.array(scheduleRescheduleSchema),
  cancelReason: z.string().optional(),
  createdAt: z.string(),
});
export type ScheduleItemResponse = z.infer<typeof scheduleItemResponseSchema>;

export const scheduleItemListResponseSchema = z.object({
  scheduleItems: z.array(scheduleItemResponseSchema),
});
export type ScheduleItemListResponse = z.infer<typeof scheduleItemListResponseSchema>;

/** SC-1's own field list. */
export const scheduleItemCreateRequestSchema = z.object({
  party: z.string().min(1).max(120),
  partyId: z.string().min(1).optional(),
  projectId: z.string().min(1).optional(),
  currency: currencyCodeSchema,
  expectedAmountMinor: z.number().int().positive(),
  expectedDate: z.string().min(1).optional(),
  milestone: z.string().min(1).max(200).optional(),
  confidence: confidenceLevelSchema,
});
export type ScheduleItemCreateRequest = z.infer<typeof scheduleItemCreateRequestSchema>;

export const scheduleItemEditRequestSchema = z.object({
  party: z.string().min(1).max(120).optional(),
  partyId: z.string().min(1).optional(),
  expectedAmountMinor: z.number().int().positive().optional(),
  milestone: z.string().min(1).max(200).optional(),
  confidence: confidenceLevelSchema.optional(),
});
export type ScheduleItemEditRequest = z.infer<typeof scheduleItemEditRequestSchema>;

/** SC-6: `newDate: null` moves the item back to undated — the key is always required so
 * "undated" is a real, explicit choice, never an accidentally-omitted field. */
export const scheduleItemRescheduleRequestSchema = z.object({
  newDate: z.string().min(1).nullable(),
});
export type ScheduleItemRescheduleRequest = z.infer<typeof scheduleItemRescheduleRequestSchema>;

/** S-14's own form spec: "Cancel requires reason." */
export const scheduleItemCancelRequestSchema = z.object({ reason: z.string().min(1).max(500) });
export type ScheduleItemCancelRequest = z.infer<typeof scheduleItemCancelRequestSchema>;

export const confidenceSplitAmountSchema = z.object({
  confirmedMinor: z.number().int(),
  likelyMinor: z.number().int(),
  tentativeMinor: z.number().int(),
});
export type ConfidenceSplitAmount = z.infer<typeof confidenceSplitAmountSchema>;

/** SC-4: 7d/30d/month/year/overdue/undated, each split by confidence (§14-6). */
export const scheduleRollupsResponseSchema = z.object({
  currency: currencyCodeSchema,
  next7Days: confidenceSplitAmountSchema,
  next30Days: confidenceSplitAmountSchema,
  thisMonth: confidenceSplitAmountSchema,
  thisYear: confidenceSplitAmountSchema,
  overdue: confidenceSplitAmountSchema,
  undated: confidenceSplitAmountSchema,
});
export type ScheduleRollupsResponse = z.infer<typeof scheduleRollupsResponseSchema>;

export const agingBucketSchema = z.object({
  amountMinor: z.number().int(),
  count: z.number().int(),
});
export type AgingBucket = z.infer<typeof agingBucketSchema>;

/** SC-7: unifies schedule items and standalone receivables. */
export const agingBucketsResponseSchema = z.object({
  currency: currencyCodeSchema,
  days1to30: agingBucketSchema,
  days31to60: agingBucketSchema,
  days61to90: agingBucketSchema,
  days90plus: agingBucketSchema,
});
export type AgingBucketsResponse = z.infer<typeof agingBucketsResponseSchema>;

// --- Profitability (WP-3.6, M14 PP-1..3, MI-3) ----------------------------------------------

/** PP-2's own three-basis list — Agreed Value is excluded (a pre-adjustment estimate, never
 * a real profit basis). */
export const revenueBasisSchema = z.enum(REVENUE_BASES);

export const projectCostEntrySchema = z.object({
  transactionId: z.string(),
  amountMinor: z.number().int(),
  at: z.string(),
  category: z.string().optional(),
});
export type ProjectCostEntry = z.infer<typeof projectCostEntrySchema>;

export const projectProfitabilityResponseSchema = z.object({
  basis: revenueBasisSchema,
  revenueMinor: z.number().int(),
  directCostsMinor: z.number().int(),
  profitMinor: z.number().int(),
  marginPercent: z.number().nullable(),
  costs: z.array(projectCostEntrySchema),
});
export type ProjectProfitabilityResponse = z.infer<typeof projectProfitabilityResponseSchema>;

export const profitabilityRollupRowSchema = z.object({
  key: z.string(),
  label: z.string(),
  revenueMinor: z.number().int(),
  directCostsMinor: z.number().int(),
  profitMinor: z.number().int(),
  marginPercent: z.number().nullable(),
  projectCount: z.number().int(),
});
export type ProfitabilityRollupRow = z.infer<typeof profitabilityRollupRowSchema>;

/** PP-3: "which clients/services are actually profitable." */
export const profitabilityRollupsResponseSchema = z.object({
  currency: currencyCodeSchema,
  basis: revenueBasisSchema,
  byClient: z.array(profitabilityRollupRowSchema),
  byServiceCategory: z.array(profitabilityRollupRowSchema),
  byProductCategory: z.array(profitabilityRollupRowSchema),
});
export type ProfitabilityRollupsResponse = z.infer<typeof profitabilityRollupsResponseSchema>;

// --- Timeline (WP-3.7, M19 TL-1/2, S-29) -----------------------------------------------------

export const timelineEntityTypeSchema = z.enum(TIMELINE_ENTITY_TYPES);

/** TL-1/2/S-29: one feed row — icon (client-side, keyed off `entityType`) + sentence + a
 * link (client-side, `entityType`/`entityId`). */
export const timelineEntryResponseSchema = z.object({
  id: z.string(),
  seq: z.number().int(),
  at: z.string(),
  entityType: timelineEntityTypeSchema,
  entityId: z.string(),
  sentence: z.string(),
  amountMinor: z.number().int().optional(),
  currency: currencyCodeSchema.optional(),
  /** WP-H1/H2 (LG-3, §18.1): WHAT made the change, so the feed can show the source iconography
   * the spec defines (✍ manual · ✦ ai · 🟢 whatsapp · ⇪ import · ⟳ recurring). The engine has
   * always logged it; the timeline projection and this contract both dropped it. */
  source: eventSourceSchema,
});
export type TimelineEntryResponse = z.infer<typeof timelineEntryResponseSchema>;

export const timelineListResponseSchema = z.object({
  entries: z.array(timelineEntryResponseSchema),
});
export type TimelineListResponse = z.infer<typeof timelineListResponseSchema>;

// --- Reports (WP-3.8, M20 RE-1/3/4, S-27/28) -------------------------------------------------

export const reportFamilySchema = z.enum(REPORT_FAMILIES);
export const yearBasisSchema = z.enum(YEAR_BASES);

/** S-27: the gallery. */
export const reportGalleryResponseSchema = z.object({
  families: z.array(z.object({ key: reportFamilySchema, label: z.string() })),
});
export type ReportGalleryResponse = z.infer<typeof reportGalleryResponseSchema>;

/** A report row's shape varies per family (RE-1's own seven families) — the engine returns
 * raw numbers only (FX-5), the web layer maps rows to columns/labels/export per family, the
 * same split `exportTable.ts`'s own `TabularData` already documents. */
export const reportRowSchema = z.record(z.string(), z.union([z.string(), z.number()]));

export const reportResultResponseSchema = z.object({
  family: reportFamilySchema,
  currency: currencyCodeSchema,
  yearBasis: yearBasisSchema,
  period: z.object({ from: z.string(), to: z.string() }),
  rows: z.array(reportRowSchema),
});
export type ReportResultResponse = z.infer<typeof reportResultResponseSchema>;

/** WP-7.4 (RE-5): "off by default; informational only" — a pure estimate, not one of the
 * REPORT_FAMILIES (Zakat lives on the Reports page, S-27/28, but is settings-gated, not part of
 * the family dispatcher). `nisabMinor`/`ratePercent` are always caller-supplied. */
export const zakatEstimateResponseSchema = z.object({
  currency: currencyCodeSchema,
  asOf: z.string(),
  qualifyingBalanceMinor: z.number().int(),
  nisabMinor: z.number().int(),
  ratePercent: z.number(),
  meetsNisab: z.boolean(),
  zakatDueMinor: z.number().int(),
});
export type ZakatEstimateResponse = z.infer<typeof zakatEstimateResponseSchema>;

// --- Snapshots (WP-5.1, RE-2, Snapshot) --------------------------------------------------
// "A stored end-of-day valuation of all balances (own funds, reserves, TPF held) enabling
// 'position over time' charts." A snapshot is a derived, recomputable aggregate, not part of
// any RE-1 report family's own `queryReport` dispatch (that stays a pure, DB-free engine
// method) — its own dedicated route/schema below.
export const snapshotRowSchema = z.object({
  date: z.string(), // YYYY-MM-DD
  currency: currencyCodeSchema,
  ownFundsMinor: z.number().int(),
  reservesMinor: z.number().int(),
  tpfHeldMinor: z.number().int(),
});
export type SnapshotRow = z.infer<typeof snapshotRowSchema>;

export const snapshotSeriesResponseSchema = z.object({
  currency: currencyCodeSchema,
  rows: z.array(snapshotRowSchema),
});
export type SnapshotSeriesResponse = z.infer<typeof snapshotSeriesResponseSchema>;

export const snapshotRunNowResponseSchema = z.object({ recorded: z.number().int().nonnegative() });
export type SnapshotRunNowResponse = z.infer<typeof snapshotRunNowResponseSchema>;

// --- Money Intelligence (WP-5.2, M17 MI-1..5, S-25 `/intel`) ------------------------------

export const miDirectionSchema = z.enum(['income', 'expense']);
export const miDimensionSchema = z.enum([...MI_INCOME_DIMENSIONS, ...MI_EXPENSE_DIMENSIONS]);

export const miRowSchema = z.object({
  key: z.string(),
  label: z.string(),
  amountMinor: z.number().int(),
});
export type MiRow = z.infer<typeof miRowSchema>;

export const miBreakdownResponseSchema = z.object({
  direction: miDirectionSchema,
  dimension: miDimensionSchema,
  currency: currencyCodeSchema,
  period: z.object({ from: z.string(), to: z.string() }),
  rows: z.array(miRowSchema),
  totalMinor: z.number().int(),
  tagId: z.string().optional(), // WP-C1.8 (SR-2)
});
export type MiBreakdownResponse = z.infer<typeof miBreakdownResponseSchema>;

/** MI-5: "a configured analysis can be named and reopened" — `config` is an opaque JSON blob
 * the web layer alone interprets (direction/dimension/currency/period/etc.); the backend just
 * stores and returns it verbatim, the same "caller owns the shape" discipline settings' own
 * jsonb column already uses. */
export const savedViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  config: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
});
export type SavedView = z.infer<typeof savedViewSchema>;

export const savedViewCreateRequestSchema = z.object({
  name: z.string().min(1).max(100),
  config: z.record(z.string(), z.unknown()),
});
export type SavedViewCreateRequest = z.infer<typeof savedViewCreateRequestSchema>;

export const savedViewListResponseSchema = z.object({ views: z.array(savedViewSchema) });
export type SavedViewListResponse = z.infer<typeof savedViewListResponseSchema>;

// --- Flow Explorer (WP-5.3, M18 MF-1/2, S-26 `/explorer`) --------------------------------

export const flowNodeSchema = z.object({
  id: z.string(),
  level: z.number().int(),
  label: z.string(),
  valueMinor: z.number().int(),
});
export type FlowNode = z.infer<typeof flowNodeSchema>;

export const flowLinkSchema = z.object({
  source: z.string(),
  target: z.string(),
  valueMinor: z.number().int(),
});
export type FlowLink = z.infer<typeof flowLinkSchema>;

// MF-2: the exact source records behind a node's total — a transaction and a transfer are
// reused verbatim (transactionResponseSchema/transferResponseSchema), never re-shaped.
export const flowRecordRefSchema = z.union([
  z.object({ kind: z.literal('transaction'), record: transactionResponseSchema }),
  z.object({ kind: z.literal('transfer'), record: transferResponseSchema }),
]);
export type FlowRecordRef = z.infer<typeof flowRecordRefSchema>;

export const moneyFlowResponseSchema = z.object({
  currency: currencyCodeSchema,
  period: z.object({ from: z.string(), to: z.string() }),
  nodes: z.array(flowNodeSchema),
  links: z.array(flowLinkSchema),
  recordsByNode: z.record(z.string(), z.array(flowRecordRefSchema)),
});
export type MoneyFlowResponse = z.infer<typeof moneyFlowResponseSchema>;

// --- Corrections (WP-4.2, AU-7/AM-3/TX-4/LG-2/LG-3, S-10 action menu) -----------------------
// Owner-facing, session-guarded web routes exposing the engine's four `correct`/split methods
// directly (`correctTransactionAmount`/`correctTransactionParty`/`relinkTransactionAccount`/
// `splitTransactionLines`) — NOT the WhatsApp/chat AM-3 impact-threshold confirm flow (that's
// a chat-specific UX concern; the web UI's own confirm dialog is the web equivalent). Every
// response echoes both the previous and new value so the caller can render the §18.4-style
// "Updated: X (was Y). History kept." ack copy without a second round-trip.

export const correctAmountRequestSchema = z.object({
  amountMinor: z.number().int().positive(),
});
export type CorrectAmountRequest = z.infer<typeof correctAmountRequestSchema>;

export const correctAmountResponseSchema = z.object({
  transactionId: z.string(),
  previousAmountMinor: z.number().int(),
  newAmountMinor: z.number().int(),
});
export type CorrectAmountResponse = z.infer<typeof correctAmountResponseSchema>;

/** AU-7: at least one of `party`/`partyId` must be given — mirrors `correctTransactionParty`'s
 * own engine-level guard so an empty request is rejected at the contract boundary too. */
export const correctPartyRequestSchema = z
  .object({
    party: z.string().min(1).max(120).optional(),
    partyId: z.string().min(1).optional(),
  })
  .refine((v) => v.party !== undefined || v.partyId !== undefined, {
    message: 'Give at least one of party or partyId to correct (AU-7)',
  });
export type CorrectPartyRequest = z.infer<typeof correctPartyRequestSchema>;

export const correctPartyResponseSchema = z.object({
  transactionId: z.string(),
  previousParty: z.string().optional(),
  previousPartyId: z.string().optional(),
  party: z.string().optional(),
  partyId: z.string().optional(),
});
export type CorrectPartyResponse = z.infer<typeof correctPartyResponseSchema>;

/** AM-3 "changes to financial obligations": account is balance-bearing (LG-4), so the
 * response echoes the account move too, same "(was X)" ack shape as the amount correction. */
export const relinkAccountRequestSchema = z.object({
  accountId: z.string().min(1),
});
export type RelinkAccountRequest = z.infer<typeof relinkAccountRequestSchema>;

export const relinkAccountResponseSchema = z.object({
  transactionId: z.string(),
  previousAccountId: z.string(),
  newAccountId: z.string(),
});
export type RelinkAccountResponse = z.infer<typeof relinkAccountResponseSchema>;

/** TX-4: Σ lines must equal the transaction's own CURRENT amount — the engine is the source
 * of truth on this (same split as `transactionCreateRequestSchema`'s own `lines` field), so
 * only the shape (min 2 lines, a real split) is validated at the contract boundary. */
export const splitLinesRequestSchema = z.object({
  lines: z.array(transactionLineSchema).min(2),
});
export type SplitLinesRequest = z.infer<typeof splitLinesRequestSchema>;

export const splitLinesResponseSchema = z.object({
  transactionId: z.string(),
  lines: z.array(transactionLineSchema),
});
export type SplitLinesResponse = z.infer<typeof splitLinesResponseSchema>;
